/**
 * @file Renders the Controlled Drugs Tracker views: Dashboard, History, and Audit Console.
 * Handles form rendering, event listeners, and UI interactions for drug transactions.
 */

import { state, appData, saveViewSettings } from '../../app/state/state.js';
import { navigate } from '../../app/routing/navigationController.js';
import {
    renderApprovalModal,
    renderSubmissionFeedback,
} from './drugApprovalModals.js';
import {
    formatDiscrepancy,
    printAuditReport,
    printAuditWorksheet,
    renderAuditDetails,
} from './drugAuditReports.js';
import { openExportModal } from './drugExportModal.js';
import {
    bindDrugHistoryTableFilterControls,
    filterDrugHistoryTransactions,
    getActiveDrugHistoryFilterKeys,
    getDrugHistoryColumnFilterRows,
    getDrugHistoryHeaderControlState,
    renderDrugHistoryFilterMenu,
} from './drugHistoryFilters.js';
import { openDrugForm as openDrugFormModal } from './drugTransactionModals.js';
import {
    closeForm,
} from '../../shared/forms/modalShell.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from '../../shared/forms/nativeFieldStyles.js';

import {
    saveDrugTransaction,
    startAudit,
    finalizeAudit,
    deleteActiveAudit,
    updateAuditProgress,
    voidTransaction,
    loadMoreDrugTransactions,
    checkPendingApprovals,
} from './drugApi.js';
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    roundToPrecision,
    sortDrugsByOrder,
    sortLocationsByOrder,
    isDrugsUiLocked,
} from "./drugUtils.js";
import {
    ALL_DRUG_HISTORY_COLUMNS,
    getDrugHistoryColumnDefinitions,
} from "./drugHistoryColumns.js";
import {
    CONTROL_ICON_MAP,
} from "../../shared/board/boardControlDefinitions.js";
import {
    getTableHeaderControlHtml,
} from "../../shared/ui/tableFilterControls.js";
import {
    renderFeatureNavSwitcher,
} from "../../shared/ui/featureNavSwitcher.js";
import {
    renderMobileRouteHeaderActions,
} from "../../shared/ui/mobileRouteHeaderActions.js";
import {
    applyDrugControlSurface,
    bindDrugNativeFieldSurface,
    DRUG_APPROVE_BUTTON_CLASSES,
    DRUG_DANGER_TEXT_BUTTON_CLASSES,
    DRUG_MODAL_AUTO_TEXTAREA_CLASSES,
    DRUG_MODAL_INPUT_CLASSES,
    DRUG_PRIMARY_BUTTON_CLASSES,
    DRUG_SECONDARY_BUTTON_CLASSES,
    requireDrugActionFieldValue,
} from "./drugControlStyles.js";

const DRUG_TAB_ACTIVE_CLASSES =
    "text-[var(--dfc-brand-accent)] font-semibold";
const DRUG_TAB_INACTIVE_CLASSES =
    "text-gray-400 hover:text-[var(--dfc-brand-primary)]";
const DRUG_HISTORY_FOCUS_CLASSES = [
    "ring-2",
    "ring-[var(--dfc-brand-primary)]",
    "bg-[color:var(--dfc-brand-primary)]/10",
];
const DRUG_HISTORY_FOCUS_CLASS_STRING = DRUG_HISTORY_FOCUS_CLASSES.join(" ");

function getDrugViewItems({
    hasAnyActionPerm,
    canViewStock,
    canViewHistory,
    canViewAudit,
}) {
    return [
        ...(hasAnyActionPerm ? [{ id: 'actions', label: 'Action Centre' }] : []),
        ...(canViewStock ? [{ id: 'stock', label: 'Stock' }] : []),
        ...(canViewHistory ? [{ id: 'history', label: 'History' }] : []),
        ...(canViewAudit ? [{ id: 'audit', label: 'Audit' }] : []),
    ];
}

function renderDrugsMobileHeader() {
    return `
        <div class="mb-4 flex items-center justify-between gap-3 md:hidden">
            <h2 class="text-2xl font-semibold text-gray-900">Controlled Drugs</h2>
            ${renderMobileRouteHeaderActions()}
        </div>
    `;
}

// --- MAIN ENTRY POINT ---

/**
 * Main entry point for rendering the drugs view.
 * Called from app/view/viewController.js when state.currentView === 'drugs'.
 * Routes to appropriate sub-view based on state.drugsView.
 */
export function renderDrugsView() {
    const container = document.getElementById('drug-board');
    if (!container) {
        console.error('Drug board container not found');
        return;
    }
    applyDrugControlSurface(container, appData);

    // Permission Checks
    const profile = state.currentUserProfile;
    const isAdmin = profile?.role === 'admin';
    const perms = profile?.drugPermissions || {};

    // Determine visibility
    const canViewStock = isAdmin || perms.viewDashboard === true || perms.drugAdmin === true;
    const canViewHistory = isAdmin || perms.viewHistory === true || perms.drugAdmin === true;
    const canViewAudit = isAdmin || perms.viewAudits === true || perms.drugAdmin === true;

    // Check if user has ANY action permissions for Action Centre access
    const hasAnyActionPerm = isAdmin || perms.drugAdmin === true ||
        perms.logUse === true ||
        perms.restock === true ||
        perms.relocate === true ||
        perms.correction === true ||
        perms.logLoss === true ||
        perms.startAudit === true;

    // View Routing logic - default to 'actions' for all users
    let currentView = state.drugsView || 'actions';
    if (currentView === 'dashboard') {currentView = 'stock';} // Normalize legacy name

    // Redirect logic based on permissions
    if (currentView === 'actions' && !hasAnyActionPerm) {
        currentView = canViewStock ? 'stock' : 'history';
        state.drugsView = currentView;
    }
    else if (currentView === 'stock' && !canViewStock) {
        currentView = hasAnyActionPerm ? 'actions' : 'history';
        state.drugsView = currentView;
    }
    else if (currentView === 'history' && !canViewHistory) {
        currentView = hasAnyActionPerm ? 'actions' : 'stock';
        state.drugsView = currentView;
    }
    else if ((currentView === 'audit' || currentView === 'auditHistory') && !canViewAudit) {
        currentView = hasAnyActionPerm ? 'actions' : 'stock';
        state.drugsView = currentView;
    }

    const viewItems = getDrugViewItems({
        hasAnyActionPerm,
        canViewStock,
        canViewHistory,
        canViewAudit,
    });

    // Generate Tabs HTML
    const tabsHtml = `
        ${renderFeatureNavSwitcher({
            id: 'drugs-mobile-section-switcher',
            label: 'Controlled Drugs section',
            items: viewItems,
            activeId: currentView === 'auditHistory' ? 'audit' : currentView,
        })}
        <div class="-mt-4 mb-6 hidden w-fit gap-8 border-b border-gray-200 md:flex">
            ${viewItems.map((item) => {
                const isActive = item.id === currentView ||
                    (item.id === 'audit' && currentView === 'auditHistory');
                const activeClass = isActive ? DRUG_TAB_ACTIVE_CLASSES : DRUG_TAB_INACTIVE_CLASSES;
                return `
                    <button class="drug-tab-btn py-2 transition-colors duration-200 focus:outline-none ${activeClass}" data-view="${item.id}">
                        ${item.label}
                    </button>`;
            }).join('')}
        </div>
    `;

    // Content container - always create fresh to ensure proper rendering
    const contentContainer = document.createElement('div');
    contentContainer.id = 'drug-content-container';

    container.innerHTML = `${renderDrugsMobileHeader()}${tabsHtml}`;
    container.appendChild(contentContainer);

    // Render appropriate sub-view FIRST
    switch (currentView) {
        case 'stock':
        case 'dashboard': // Legacy support
            renderStockView(contentContainer);
            break;
        case 'actions':
            renderActionCentre(contentContainer);
            break;
        case 'history':
            renderDrugHistoryTable(contentContainer);
            break;
        case 'audit':
        case 'auditHistory':
            renderAuditConsole(contentContainer);
            break;
        default:
            state.drugsView = hasAnyActionPerm ? 'actions' : 'stock';
            if (hasAnyActionPerm) {
                renderActionCentre(contentContainer);
            } else {
                renderStockView(contentContainer);
            }
    }

    // Tab click handlers (attach AFTER rendering to ensure buttons exist)
    container.querySelectorAll('.drug-tab-btn, [data-feature-nav-option]').forEach((btn) => {
        // Allow clicking 'actions' now
        // if (btn.dataset.view === 'actions') return;

        btn.onclick = () => {
            const view = btn.dataset.view || btn.dataset.featureNavOption;
            navigate('drugs', view);
        };
    });
}

/**
 * Opens the drug transaction form modal.
 * Delegates to the Drugs-owned transaction modal.
 * @param {'use'|'relocate'|'restock'|'correction'|'loss'} type - Transaction type.
 */
export function openDrugForm(type) {
    if (type === 'restock') {
        const check = isRestockAllowed();
        if (!check.allowed) {
            openConfirmationModal(
                'Audit Required',
                `Cannot restock.<br><br>${check.reason}<br><br>Please complete a stock audit to enable restocking.`,
                () => { },
                false,
                'OK'
            );
            return;
        }
    }
    openDrugFormModal(type);
}

/**
 * Renders the stock view (grid showing current stock levels).
 * @param {HTMLElement} container - Container element to render into.
 */
export function renderStockView(container) {
    if (!container) {return;}
    container.innerHTML = '';

    const locations = sortLocationsByOrder(appData.config?.drugs?.locations || []);
    const inventory = appData.drugInventory || [];

    if (locations.length === 0) {
        container.innerHTML = `
            <div class="text-center py-12">
                <p class="text-gray-500">No locations configured. Please configure locations in Settings.</p>
            </div>
        `;
        return;
    }

    const activeAudit = appData.activeAudit;
    const isLocked = !!activeAudit;

    // Header (no action buttons - users must use Action Centre)
    // ADDED: Print Audit Worksheet Button (Icon Only)
    const headerHtml = `
        <div class="mb-6">
            <div class="flex justify-between items-center mb-3">
                <h1 class="text-2xl font-bold text-gray-800">Controlled Drugs Stock</h1>
                <button id="btn-print-worksheet" class="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:text-blue-600 focus:outline-none dark:text-gray-400 dark:hover:text-blue-400" title="Print Audit Worksheet" aria-label="Print audit worksheet">
                    <span class="material-symbols-outlined text-xl">print</span>
                </button>
            </div>
            ${isLocked ? `
                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div class="flex items-center">
                        <span class="material-symbols-outlined text-yellow-600 mr-2">lock</span>
                        <div>
                            <p class="text-sm font-medium text-yellow-800">
                                Audit in Progress - Transactions are locked
                            </p>
                            <p class="text-xs text-yellow-700 mt-1">
                                Please finalise or cancel the audit in the Audit tab to resume transactions.
                            </p>
                        </div>
                    </div>
                </div>
            ` : ''}
        </div>
    `;

    // Stock Grid
    let gridHtml = `
        <div class="bg-white rounded-lg shadow overflow-hidden">
            <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider !sticky left-0 bg-gray-50 z-10">Drug</th>
                        ${locations.map((loc) => `<th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">${sanitizeHTML(loc.name)}</th>`).join('')}
                        <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
    `;

    // Sort drugs by admin-configured order (with fallback to name)
    const activeDrugs = sortDrugsByOrder(
        inventory.filter((d) => d.isActive !== false)
    );

    if (activeDrugs.length === 0) {
        gridHtml += `
            <tr>
                <td colspan="${locations.length + 2}" class="px-6 py-10 text-center text-gray-500">
                    No active drugs configured.
                </td>
            </tr>
        `;
    } else {
        activeDrugs.forEach((drug) => {
            let totalStock = 0;
            const displayName = drug.shortName || drug.name || 'Unknown';

            gridHtml += `<tr class="hover:bg-gray-50">`;
            gridHtml += `<td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">${sanitizeHTML(displayName)}</td>`;

            locations.forEach((loc) => {
                const stock = roundToPrecision(drug.stockLevels?.[loc.id] || 0);
                totalStock += stock;

                // Threshold Check
                const thresholds = drug.minStockThreshold?.[loc.id];
                let colorClass = 'text-green-600 bg-green-50';

                if (stock < 0) {
                    colorClass = 'text-red-800 bg-red-100 font-bold border border-red-300';
                } else if (thresholds && stock <= thresholds.warning) {
                    colorClass = 'text-red-600 bg-red-50 font-bold';
                } else if (thresholds && stock <= thresholds.reminder) {
                    colorClass = 'text-yellow-600 bg-yellow-50';
                }

                // Forecast - Always show if forecast data exists (mid grey, no color coding)
                // Only if enabled in config
                let forecastHtml = '';
                if (appData.config?.drugs?.forecastEnabled) {
                    const forecast = drug.forecast?.[loc.id];
                    if (forecast && forecast.daysRemaining !== undefined && forecast.daysRemaining !== null && !isNaN(forecast.daysRemaining) && forecast.daysRemaining < 999) {
                        const days = Math.round(forecast.daysRemaining);
                        forecastHtml = `<div class="text-xs text-gray-500 mt-1">${days} days</div>`;
                    }
                }

                gridHtml += `
                    <td class="px-6 py-4 whitespace-nowrap text-center">
                        <div class="flex flex-col items-center">
                            <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${colorClass}">
                                ${roundToPrecision(stock)}
                            </div>
                            ${forecastHtml}
                        </div>
                    </td>
                `;
            });

            // Total threshold check
            const totalThresholds = drug.minStockThreshold?.['total'];
            let totalColorClass;
            if (totalStock < 0) {
                totalColorClass = 'text-red-800 font-bold';
            } else if (totalThresholds && totalStock <= totalThresholds.warning) {
                totalColorClass = 'text-red-600 font-bold';
            } else if (totalThresholds && totalStock <= totalThresholds.reminder) {
                totalColorClass = 'text-yellow-600';
            } else {
                totalColorClass = 'text-green-600';
            }

            // Use backend total forecast directly so frontend stays aligned
            // with the canonical backend calculation.
            let totalForecastHtml = '';

            if (appData.config?.drugs?.forecastEnabled) {
                const totalForecast = drug.forecast?.total;
                if (totalForecast && totalForecast.daysRemaining !== undefined &&
                    totalForecast.daysRemaining !== null &&
                    !isNaN(totalForecast.daysRemaining) &&
                    totalForecast.daysRemaining < 999) {
                    const days = Math.round(totalForecast.daysRemaining);
                    totalForecastHtml = `<div class="text-xs text-gray-500 mt-1">${days} days</div>`;
                }
            }

            gridHtml += `
                <td class="px-6 py-4 whitespace-nowrap text-center">
                    <div class="flex flex-col items-center">
        <div class="text-sm font-bold ${totalColorClass}">${roundToPrecision(totalStock)}</div>
                        ${totalForecastHtml}
                    </div>
                </td>
            `;
            gridHtml += `</tr>`;
        });
    }

    gridHtml += `</tbody></table></div></div>`;

    container.innerHTML = headerHtml + gridHtml;

    // Attach listener for Print Worksheet
    const printBtn = container.querySelector('#btn-print-worksheet');
    if (printBtn) {
        printBtn.onclick = () => printAuditWorksheet();
    }
}

// --- HELPER: Resolve Role Name ---
// --- HELPER: Resolve Role Name ---
function getApproverRoleName(roleId) {
    if (!roleId) {return 'Approver';}
    const role = (appData.drugRoles || []).find(r => r.id === roleId);
    return role ? sanitizeHTML(role.name) : 'Approver'; // Fallback
}

// --- HELPER: Resolve Approver Names (Human Readable) ---
function resolveApproverNames(roleId, specificUserIds = []) {
    let idsToResolve = specificUserIds;

    // If no specific IDs provided (or empty), fallback to Role Definition
    if (!idsToResolve || idsToResolve.length === 0) {
        if (!roleId) {return 'Unknown';}

        // 1. Find the Role Definition
        const role = (appData.drugRoles || []).find(r => r.id === roleId);
        if (!role) {return 'Unknown Role';}

        // 2. Get Member IDs
        idsToResolve = role.memberIds || [];
        if (idsToResolve.length === 0) {return sanitizeHTML(role.name) + ' (No members)';}
    }

    // 3. Resolve Names from User Data (Sorted)
    const users = idsToResolve.map(uid => (appData.users || []).find(u => u.id === uid)).filter(u => u);

    // In-line sort logic (simple copy) to avoid async import in sync render helper
    users.sort((a, b) => {
        const orderA = a.drugSortOrder !== undefined && a.drugSortOrder !== null ? Number(a.drugSortOrder) : 999;
        const orderB = b.drugSortOrder !== undefined && b.drugSortOrder !== null ? Number(b.drugSortOrder) : 999;
        if (orderA !== orderB) {return orderA - orderB;}
        return (a.name || '').localeCompare(b.name || '');
    });

    const names = users.map(user => {
        return user.name.split(' ')[0]; // First name only for brevity
    });

    // If still empty despite finding role/ids
    if (names.length === 0) {
        // Fallback to Role Name if we have the ID but couldn't find users (maybe inactive)
        const role = (appData.drugRoles || []).find(r => r.id === roleId);
        return role ? sanitizeHTML(role.name) : 'Unknown';
    }

    // Join with commas
    return sanitizeHTML(names.join(', '));
}

// --- HISTORY TABLE ---

function renderDrugHistoryColumnToggleOption(key, visibleColumns) {
    const label = ALL_DRUG_HISTORY_COLUMNS[key] || key;
    const checkedAttr = visibleColumns.includes(key) ? 'checked' : '';
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} w-full rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50 focus-within:bg-gray-50" data-native-checkbox-label>
            <input type="checkbox" class="${NATIVE_CHECKBOX_CLASSES} column-toggle-checkbox-drugs-history" data-key="${key}" ${checkedAttr}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span class="min-w-0 flex-1 truncate">${sanitizeHTML(label)}</span>
        </label>
    `;
}

/**
 * Renders the transaction history table with column controls.
 * @param {HTMLElement} container - Container element to render into.
 */
export function renderDrugHistoryTable(container) {
    if (!container) {return;}
    applyDrugControlSurface(container, appData);
    container.innerHTML = '';

    const isAdmin = state.currentUserProfile?.role === 'admin';
    const perms = state.currentUserProfile?.drugPermissions || {};

    const rawTransactions = appData.drugTransactions || [];
    const pendingApprovals = appData.allPendingApprovals || [];

    // Create lookup map for approvals based on targetId (for linking to Soft Blocks)
    const approvalMap = {};
    pendingApprovals.forEach(a => {
        if (a.targetId) {approvalMap[a.targetId] = a;}
    });

    // --- 1. Adapter: Convert Pending Approvals to Pseudo-Transactions ---
    const pseudoTransactions = pendingApprovals.filter(approval => {
        // Fix for Double Entries:
        // If an approval is linked to an existing transaction (e.g., Soft Block / Post-Action),
        // the real transaction is already in rawTransactions with status 'pending_finalization'.
        // We must NOT render a second "pseudo-transaction" here.
        if (approval.targetId && rawTransactions.some(tx => tx.id === approval.targetId)) {
            // EXCEPTION: Void transactions target an existing transaction but represent a NEW reversal.
            // So we MUST show the pseudo-transaction for the void request.
            if (approval.workflowType === 'drug_void_transaction') {
                // Future-Proofing: If the target is ALREADY a 'void' transaction, it means the reversal exists (Soft Block).
                // In that case, don't show the pseudo-transaction.
                const targetTx = rawTransactions.find(tx => tx.id === approval.targetId);
                if (targetTx && targetTx.type === 'void') {
                    return false;
                }
                return true;
            }
            return false;
        }
        return true;
    }).map(approval => {
        const data = approval.payload || {};
        // Adapter to extract items array from approval payload (drug_transactions stores items in 'payload')
        const items = Array.isArray(data.payload) ? data.payload : [];

        return {
            id: approval.id,
            type: approval.workflowType === 'drug_void_transaction' ? 'void' : (approval.workflowType || '').replace('drug_', ''),
            status: 'pending_approval',
            transactionDate: data.transactionDate || '', // Use the planned date from payload
            createdAt: approval.createdAt, // Include creation time for sorting tie-breaker
            performedBy: approval.requesterId,
            payload: items,
            patientName: data.patientName || null,
            batchCode: data.batchCode || null,
            provider: data.provider || null,
            orderNumber: data.orderNumber || null,
            supplier: data.supplier || null,
            justification: data.justification || null,
            fromLocation: data.fromLocation || null,
            toLocation: data.toLocation || null,
            voidedTransactionId: data.voidedTransactionId || null,
            voidReason: data.voidReason || null,
            _isPseudo: true,
            _approverIds: approval.approverIds,
            _badging: {
                type: 'hard',
                roleId: approval.currentApproverRole
            }
        };
    });

    // --- 2. Merge & Inject Badging for Real Transactions ---
    const transactions = [...rawTransactions, ...pseudoTransactions].map(tx => {
        // If already pseudo, return as is (badging already set)
        if (tx._isPseudo) {return tx;}

        // Check for Soft Block (status: 'pending_finalization')
        if (tx.status === 'pending_finalization') {
            const linkedApproval = approvalMap[tx.id];
            return {
                ...tx,
                _approverIds: linkedApproval ? linkedApproval.approverIds : null,
                _badging: {
                    type: 'soft',
                    roleId: tx.currentApproverRole // Stamped by backend
                }
            };
        }
        return tx;
    });

    // Get view settings
    const settings = state.viewSettings?.drugsHistory;
    if (!settings) {
        console.error("renderDrugHistoryTable: View settings not found.");
        return;
    }
    const { filters = {}, sortBy, sortDirection, columnWidths = {} } = settings;
    let { visibleColumns = [] } = settings;

    // Remove deprecated "details" column if present (replaced by "drug" and "location")
    if (visibleColumns.includes('details')) {
        visibleColumns = visibleColumns.filter(col => col !== 'details');
        // Add drug and location if not already present
        if (!visibleColumns.includes('drug')) {visibleColumns.push('drug');}
        if (!visibleColumns.includes('location')) {visibleColumns.push('location');}
        settings.visibleColumns = visibleColumns;
        saveViewSettings();
    }
    const focusedHistoryId = state.drugHistoryFocusId || null;

    // Filter transactions by type
    let filteredTransactions = transactions;

    // RULE 1: Only show Finalized Audit transactions in history
    // RULE 2: Filter out legacy audit lifecycle events (audit_start, audit_cancel, audit_finalise)
    // We filter OUT any 'audit' type transaction if its linked audit is NOT finalized
    filteredTransactions = filteredTransactions.filter(tx => {
        // Strict cleanup of legacy or direct lifecycle events
        if (['audit_start', 'audit_cancel', 'audit_finalise'].includes(tx.type)) {
            return false;
        }

        if (tx.type === 'audit') {
            const audit = appData.drugAudits?.find(a => a.id === tx.auditId);
            return audit && audit.status === 'finalized';
        }
        return true;
    });

    const visibleColumnDefinitions = getDrugHistoryColumnDefinitions(visibleColumns);
    const tableFilterSourceRows = getDrugHistoryColumnFilterRows(filteredTransactions);

    // Apply column filters
    const activeFilterKeys = getActiveDrugHistoryFilterKeys(settings, visibleColumnDefinitions);
    const hasDrugFilter = activeFilterKeys.includes('drug');
    const drugFilterValues = hasDrugFilter ? filters.drug : [];
    if (activeFilterKeys.length > 0) {
        filteredTransactions = filterDrugHistoryTransactions(
            filteredTransactions,
            visibleColumnDefinitions,
            settings,
        );

        // If filtering by drug, split transactions to show only matching drug items
        if (hasDrugFilter) {
            const splitTransactions = [];
            filteredTransactions.forEach(tx => {
                // Filter payload to only include matching drugs
                const matchingItems = tx.payload.filter(item =>
                    drugFilterValues.includes(String(item.drugId))
                );

                if (matchingItems.length > 0) {
                    // For relocate, we need to keep pairs together
                    if (tx.type === 'relocate') {
                        // Group by drugId and keep source/dest pairs
                        const drugGroups = {};
                        matchingItems.forEach(item => {
                            if (!drugGroups[item.drugId]) {
                                drugGroups[item.drugId] = { source: null, dest: null };
                            }
                            if (item.quantity < 0) {
                                drugGroups[item.drugId].source = item;
                            } else {
                                drugGroups[item.drugId].dest = item;
                            }
                        });

                        // Create a split transaction for each drug group
                        Object.keys(drugGroups).forEach(drugId => {
                            const group = drugGroups[drugId];
                            if (group.source && group.dest) {
                                splitTransactions.push({
                                    ...tx,
                                    _displayId: `${tx.id}_${drugId}`, // Unique display ID for split transaction
                                    _originalId: tx.id, // Keep original ID for void button
                                    payload: [group.source, group.dest]
                                });
                            }
                        });
                    } else {
                        // For other types, create one split transaction per matching drug
                        const drugGroups = {};
                        matchingItems.forEach(item => {
                            if (!drugGroups[item.drugId]) {
                                drugGroups[item.drugId] = [];
                            }
                            drugGroups[item.drugId].push(item);
                        });

                        Object.keys(drugGroups).forEach(drugId => {
                            splitTransactions.push({
                                ...tx,
                                _displayId: `${tx.id}_${drugId}`, // Unique display ID for split transaction
                                _originalId: tx.id, // Keep original ID for void button
                                payload: drugGroups[drugId]
                            });
                        });
                    }
                }
            });
            filteredTransactions = splitTransactions;
        }
    }

    // RECURSIVE FETCH LOGIC:
    // If we have fewer than targeted visible rows AND more data exists on the server,
    // automatically trigger the next batch fetch to satisfy the UI requirement.
    const targetVisible = state.drugHistoryPagination.minVisibleItems || 50;
    if (filteredTransactions.length < targetVisible && state.drugHistoryPagination?.hasMore) {
        setTimeout(() => {
            loadMoreDrugTransactions(state.drugHistoryPagination.lastDoc);
        }, 100);
    }

    // Sort transactions
    filteredTransactions.sort((a, b) => {
        if (sortBy === 'transactionDate') {
            const getTime = (t) => {
                let dateObj;
                // Check if it's a simple date string (YYYY-MM-DD)
                const isDateString = typeof t.transactionDate === 'string' && t.transactionDate.length === 10;

                if (t.transactionDate?.toDate) {dateObj = t.transactionDate.toDate();}
                else {dateObj = new Date(t.transactionDate);}

                // If invalid date, fallback to 0
                if (isNaN(dateObj)) {return 0;}

                // Check if it represents an "All Day" or "Date Only" value
                // 1. Explicitly strictly a YYYY-MM-DD string
                // 2. Local Midnight
                // 3. UTC Midnight (which is how YYYY-MM-DD is parsed)
                const isMidnightLocal = dateObj.getHours() === 0 && dateObj.getMinutes() === 0 && dateObj.getSeconds() === 0;
                const isMidnightUTC = dateObj.getUTCHours() === 0 && dateObj.getUTCMinutes() === 0 && dateObj.getUTCSeconds() === 0;

                const isDateOnly = isDateString || isMidnightLocal || isMidnightUTC;

                // If it's a date-only value, prefer the creation timestamp for sorting order
                if (isDateOnly && t.createdAt) {
                    let createdObj;
                    if (t.createdAt?.toDate) {createdObj = t.createdAt.toDate();}
                    else {createdObj = new Date(t.createdAt);}
                    if (!isNaN(createdObj)) {return createdObj.getTime();}
                }

                return dateObj.getTime();
            };

            const tA = getTime(a);
            const tB = getTime(b);

            return sortDirection === 'asc' ? tA - tB : tB - tA;
        }

        // ... Other sort types ...
        let valA, valB;
        if (sortBy === 'type') {
            valA = a.type || '';
            valB = b.type || '';
        } else if (sortBy === 'performedBy') {
            const userA = appData.users.find(u => u.id === a.performedBy);
            const userB = appData.users.find(u => u.id === b.performedBy);

            const orderA = userA && userA.drugSortOrder !== undefined && userA.drugSortOrder !== null ? Number(userA.drugSortOrder) : 999;
            const orderB = userB && userB.drugSortOrder !== undefined && userB.drugSortOrder !== null ? Number(userB.drugSortOrder) : 999;

            if (orderA !== orderB) {
                return sortDirection === 'asc' ? orderA - orderB : orderB - orderA;
            }

            valA = userA?.name || '';
            valB = userB?.name || '';
        } else if (sortBy === 'status') {
            valA = a.status || '';
            valB = b.status || '';
        } else {
            valA = a[sortBy] || '';
            valB = b[sortBy] || '';
        }

        const comparison = String(valA).localeCompare(String(valB));
        return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Header & Controls
    const headerHtml = `
        <div class="flex justify-between items-center mb-4">
            <h1 class="text-2xl font-bold text-gray-800">Transaction History</h1>
            <div class="flex gap-0 items-center">
                <div class="relative">

                    <button id="column-toggle-btn-drugs-history" type="button" class="inline-flex h-10 w-10 appearance-none items-center justify-center border-0 bg-transparent p-0 text-gray-500 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none dark:text-gray-400" style="border: 0; background: transparent;" title="Toggle Columns" aria-label="Toggle columns" aria-expanded="false">
                        <span class="material-symbols-outlined pointer-events-none text-[20px]">${CONTROL_ICON_MAP.columns}</span>
                    </button>
                    <div id="column-toggle-dropdown-drugs-history" class="hidden absolute right-0 z-30 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 text-sm shadow-xl">
                        <div class="px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Columns</div>
                        <div class="space-y-1">
                            ${Object.keys(ALL_DRUG_HISTORY_COLUMNS).map(key => renderDrugHistoryColumnToggleOption(key, visibleColumns)).join('')}
                        </div>
                    </div>
                </div>
                ${(isAdmin || perms.drugAdmin || perms.canExport) ? `
                <button id="btn-export" type="button" class="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:text-blue-600 focus:outline-none dark:text-gray-400 dark:hover:text-blue-400" title="Export" aria-label="Export">
                    <span class="material-symbols-outlined text-[20px]">download</span>
                </button>` : ''}
            </div>

        </div>
    `;

    // Table Header
    const headerControlState = getDrugHistoryHeaderControlState(settings);
    const getColumnStyle = (key) => {
        const savedWidth = columnWidths[key];
        let style = '';
        if (savedWidth) {
            style = `width: ${savedWidth}px;`;
        } else if (key === 'notes') {
            style = 'max-width: 200px;';
        } else if (key === 'actions') {
            style = 'width: 80px; min-width: 80px;';
        }
        return style ? `style="${style}"` : '';
    };

    let tableHtml = `
        <div class="relative rounded-lg bg-white shadow" data-drug-history-table-wrapper>
            <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        ${visibleColumns.map(key => {
        const title = ALL_DRUG_HISTORY_COLUMNS[key] || key;
        const column = visibleColumnDefinitions.find((definition) => definition.key === key) || {
            key,
            label: title,
            filter: { kind: 'none' },
        };
        const isActions = key === 'actions';

        if (isActions) {
            return `<th scope="col" class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sortable relative group" draggable="true" data-key="${key}" ${getColumnStyle(key)}>
                                    <span>${title}</span>
                                </th>`;
        }

        return `<th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sortable relative group" draggable="true" data-key="${key}" ${getColumnStyle(key)}>
                                ${getTableHeaderControlHtml(column, headerControlState)}
                                <div class="table-resizer hidden sm:block" data-key="${key}"></div>
                            </th>`;
    }).join('')}
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
    `;

    // Calculate user's active roles for permission checks
    const userRoleIds = appData.drugRoles
        ?.filter(r => r.memberIds?.includes(state.currentUser?.uid))
        .map(r => r.id) || [];


    if (filteredTransactions.length === 0) {
        tableHtml += `<tr><td colspan="${visibleColumns.length}" class="px-4 py-10 text-center text-gray-500">No transactions found.</td></tr>`;
    } else {
        // Group audit transactions with their corrections
        // Group audit transactions with their corrections
        const processedAuditIds = new Set();
        const processedTxIds = new Set();

        // Single pass through sorted transactions
        filteredTransactions.forEach((tx) => {
            // Skip if already processed
            if (processedTxIds.has(tx.id)) {return;}

            // Check if this is an audit-related transaction (Audit or Correction linked to an Audit)
            const isAudit = tx.type === 'audit' && tx.auditId;
            const isAuditCorrection = tx.type === 'correction' && tx.auditId;

            if (isAudit || isAuditCorrection) {
                const auditId = tx.auditId;

                // If we've already processed this audit group, skip
                if (processedAuditIds.has(auditId)) {return;}

                // Mark audit group as processed
                processedAuditIds.add(auditId);

                // Finding all components of this group from the filtered list is tricky if paginated?
                // But for now we work with `filteredTransactions`.
                // We should try to find the actual Audit Transaction if it exists in the full list or filtered list
                // to get the summary details.
                const auditTx = (isAudit ? tx : filteredTransactions.find(t => t.type === 'audit' && t.auditId === auditId))
                    || (appData.drugTransactions || []).find(t => t.type === 'audit' && t.auditId === auditId);

                const auditDetail = appData.drugAudits?.find(a => a.id === auditId);

                // If we can't find the main audit record (transaction or doc), we might just render the correction individually?
                // But usually they should exist.

                // Gather corrections for this audit
                // We check both filteredTransactions (prioritize) and appData if needed?
                // No, we should only render what's in the current view/filter context usually,
                // BUT the "Group" logic implies showing the whole package.
                // The previous logic filtered `filteredTransactions`. Let's stick to that to respect filters.
                const corrections = filteredTransactions
                    .filter(t => t.type === 'correction' && t.auditId === auditId)
                    .sort((a, b) => {
                        const dateA = a.transactionDate ? (typeof a.transactionDate === 'string' ? new Date(a.transactionDate) : (a.transactionDate.toDate ? a.transactionDate.toDate() : new Date(a.transactionDate))) : new Date(0);
                        const dateB = b.transactionDate ? (typeof b.transactionDate === 'string' ? new Date(b.transactionDate) : (b.transactionDate.toDate ? b.transactionDate.toDate() : new Date(b.transactionDate))) : new Date(0);
                        return dateA - dateB;
                    });

                // Mark all components as processed so we don't render them again
                if (auditTx && filteredTransactions.includes(auditTx)) {processedTxIds.add(auditTx.id);}
                corrections.forEach(c => processedTxIds.add(c.id));

                // Render Corrections First
                corrections.forEach(correctionTx => {
                    // Render Transaction Row
                    // --- BADGE LOGIC ---
                    let statusHtml = formatTransactionStatus(correctionTx); // Default
                    let rowHighlightClass = '';

                    if (correctionTx._badging) {
                        const roleName = getApproverRoleName(correctionTx._badging.roleId);

                        if (correctionTx._badging.type === 'hard') {
                            // Permission Check for Badging Click
                            const isApprover = isAdmin || userRoleIds.includes(correctionTx._badging.roleId);
                            const cursorClass = isApprover ? 'cursor-pointer hover:opacity-80 js-approval-badge' : 'cursor-default';
                            const linkedApprovalId = correctionTx.id; // Correct? Pseudo-tx ID IS the approval ID!

                            // Orange Badge (Hard Block)
                            statusHtml = `
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ${cursorClass}"
                                  title="${isApprover ? 'Click to Review' : `Waiting for ${roleName}`}"
                                  ${isApprover ? `data-approval-id="${linkedApprovalId}"` : ''}>
                                <span class="w-2 h-2 mr-1.5 bg-orange-400 rounded-full animate-pulse"></span>
                                Pending Approval
                            </span>
                            <div class="text-[10px] text-gray-400 mt-1 text-right mr-1">Target: ${roleName}</div>
                        `;
                            rowHighlightClass = 'bg-orange-50/30'; // Very subtle tint
                        } else if (correctionTx._badging.type === 'soft') {
                            // Blue Badge (Soft Block)
                            // Soft blocks might be finalizable by others?
                            // Usually strict to approver role too.
                            const isApprover = isAdmin || userRoleIds.includes(correctionTx._badging.roleId);
                            // Link logic: pseudo-tx ID is approval ID
                            const linkedApprovalId = correctionTx.id;

                            const cursorClass = isApprover ? 'cursor-pointer hover:opacity-80 js-approval-badge' : 'cursor-default';

                            statusHtml = `
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ${cursorClass}"
                                  title="${isApprover ? 'Click to Finalise' : `Waiting for ${roleName}`}"
                                  ${isApprover ? `data-approval-id="${linkedApprovalId}"` : ''}>
                                <span class="w-2 h-2 mr-1.5 bg-blue-400 rounded-full"></span>
                                Pending Finalisation
                            </span>
                             <div class="text-[10px] text-gray-400 mt-1 text-right mr-1">Target: ${roleName}</div>
                        `;
                        }
                    }

                    const isFocusedHistoryRow = focusedHistoryId === correctionTx.id;
                    const focusClass = isFocusedHistoryRow ? DRUG_HISTORY_FOCUS_CLASS_STRING : '';
                    tableHtml += `<tr class="hover:bg-gray-50 ${rowHighlightClass} ${focusClass}" data-history-id="${correctionTx.id}">`;

                    visibleColumns.forEach(key => {
                        let cellContent = '';
                        let cellClass = 'px-4 py-4 text-sm'; // Increased padding for comfort

                        if (key === 'transactionDate') {
                            cellContent = formatDate(correctionTx.transactionDate);
                            cellClass += ' whitespace-nowrap text-gray-500';
                        } else if (key === 'type') {
                            const typeLabel = correctionTx.type.replace('drug_', '').replace('_', ' '); // Clean up 'drug_use' -> 'use'
                            cellContent = `<span class="capitalize font-semibold text-gray-700">${sanitizeHTML(typeLabel)}</span>`;
                            cellClass += ' whitespace-nowrap';
                        } else if (key === 'drug') {
                            cellContent = formatTransactionDrugs(correctionTx);
                            cellClass += ' text-gray-900';
                        } else if (key === 'location') {
                            cellContent = formatTransactionLocations(correctionTx);
                            cellClass += ' text-gray-500';
                        } else if (key === 'quantity') {
                            cellContent = formatTransactionQuantities(correctionTx);
                        } else if (key === 'balance') {
                            cellContent = formatTransactionBalances(correctionTx);
                        } else if (key === 'notes') {
                            cellContent = formatTransactionNotes(correctionTx);
                            cellClass += ' text-gray-500 italic';
                            if (!columnWidths.notes) {
                                cellClass += ' max-w-[200px]';
                            }
                        } else if (key === 'performedBy') {
                            const user = appData.users.find((u) => u.id === correctionTx.performedBy);
                            cellContent = user ? sanitizeHTML(user.name) : 'Unknown';
                            cellClass += ' whitespace-nowrap text-gray-500';
                        } else if (key === 'status') {
                            cellContent = statusHtml;
                            cellClass += ' whitespace-nowrap';
                        } else if (key === 'actions') {
                            // Action buttons
                            if (correctionTx._isPseudo) {
                                // Pending Approvals don't have actions here (managed in Action Centre)
                                cellContent = '<span class="text-xs text-gray-400 italic">View Action Centre</span>';
                            } else {
                                const canVoid = canVoidTransaction(correctionTx);
                                cellContent = (correctionTx.status === 'approved' && canVoid) ?
                                    `<button data-tx-id="${correctionTx.id}" class="btn-void-tx text-gray-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50" title="Void Transaction">
                                    <span class="material-symbols-outlined text-lg">block</span>
                                </button>` : '';
                            }
                            cellClass += ' whitespace-nowrap text-right font-medium';
                        }

                        const cellStyle = (key === 'notes' && !columnWidths.notes) ? 'style="overflow: hidden; text-overflow: ellipsis;"' : '';
                        tableHtml += `<td class="${cellClass}" ${cellStyle}>${cellContent}</td>`;
                    });

                    tableHtml += `</tr>`;
                });

                // Render Audit Summary Row (Only if we found the audit transaction)
                if (auditTx) {
                    const auditNumber = auditDetail?.auditNumber || auditTx.auditNumber || 'N/A';
                    const resultsStatus = auditDetail?.resultsStatus || auditTx.resultsStatus || 'unknown';
                    const resultsLabel = resultsStatus === 'reconciled' ? 'Reconciled' : 'Discrepancy';
                    const resultsColor = resultsStatus === 'reconciled' ? 'text-green-600' : 'text-red-600';
                    const startedBy = appData.users?.find(u => u.id === auditTx.performedBy);
                    const startedByName = startedBy ? sanitizeHTML(startedBy.name) : 'Unknown';
                    const statusHtml = formatTransactionStatus(auditTx);

                    const auditDetailsRowId = `audit-details-row-${sanitizeHTML(auditId)}`;
                    tableHtml += `<tr class="bg-blue-50 hover:bg-blue-100 cursor-pointer audit-summary-row" data-audit-id="${auditId}">`;

                    visibleColumns.forEach(key => {
                        let cellContent = '';
                        let cellClass = 'px-4 py-3 text-sm';

                        if (key === 'transactionDate') {
                            cellContent = formatDate(auditTx.transactionDate);
                            cellClass += ' whitespace-nowrap text-gray-700 font-medium';
                        } else if (key === 'type') {
                            cellContent = 'Audit';
                            cellClass += ' whitespace-nowrap font-medium capitalize text-gray-900';
                        } else if (key === 'drug') {
                            cellContent = `<span class="${resultsColor} font-medium">${resultsLabel}</span>`;
                            cellClass += ' text-gray-900';
                        } else if (key === 'balance') {
                            cellContent = '-'; // Audits don't show balance
                            cellClass += ' text-gray-400';
                        } else if (key === 'location') {
                            cellContent = '-';
                            cellClass += ' text-gray-400';
                        } else if (key === 'quantity') {
                            cellContent = '-';
                            cellClass += ' text-gray-400';
                        } else if (key === 'notes') {
                            cellContent = `Audit #${auditNumber}`;
                            cellClass += ' text-gray-500';
                            if (!columnWidths.notes) {
                                cellClass += ' max-w-[200px]';
                            }
                        } else if (key === 'performedBy') {
                            cellContent = startedByName;
                            cellClass += ' whitespace-nowrap text-gray-700';
                        } else if (key === 'status') {
                            cellContent = statusHtml;
                            cellClass += ' whitespace-nowrap';
                        } else if (key === 'actions') {
                            cellContent = `<span class="inline-flex items-center justify-end gap-2 align-middle"><button class="btn-print-audit inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:text-blue-600 focus:outline-none dark:text-gray-400 dark:hover:text-blue-400" data-audit-id="${auditId}" title="Print Audit" aria-label="Print audit"><span class="material-symbols-outlined text-lg">print</span></button><button type="button" class="btn-toggle-audit-details inline-flex h-8 w-4 items-center justify-center text-gray-400 transition-colors hover:text-blue-600 focus:outline-none focus-visible:text-blue-600" data-audit-id="${auditId}" aria-expanded="false" aria-controls="${auditDetailsRowId}" title="Toggle audit details" aria-label="Toggle audit details"><span class="material-symbols-outlined text-sm audit-expand-icon" aria-hidden="true">expand_more</span></button></span>`;
                            cellClass += ' whitespace-nowrap text-right font-medium';
                        }

                        const cellStyle = (key === 'notes' && !columnWidths.notes) ? 'style="overflow: hidden; text-overflow: ellipsis;"' : '';
                        tableHtml += `<td class="${cellClass}" ${cellStyle}>${cellContent}</td>`;
                    });

                    tableHtml += `</tr>`;

                    // Expandable details row (initially hidden)
                    tableHtml += `<tr id="${auditDetailsRowId}" class="audit-details-row hidden" data-audit-id="${auditId}"><td colspan="${visibleColumns.length}" class="px-4 py-4 bg-white border-t border-gray-200">`;
                    tableHtml += renderAuditDetails(auditDetail || { id: auditId, ...auditTx });
                    tableHtml += `</td></tr>`;
                }

            } else {
                // STANDARD TRANSACTION RENDER (Use, Restock, Relocate, etc.)
                processedTxIds.add(tx.id);

                const quantitiesHtml = formatTransactionQuantities(tx);
                const notesHtml = formatTransactionNotes(tx);
                let statusHtml = formatTransactionStatus(tx);
                const user = appData.users.find((u) => u.id === tx.performedBy);
                const userName = user ? sanitizeHTML(user.name) : 'Unknown User';

                // --- BADGE LOGIC ---
                let rowHighlightClass = '';
                if (tx._badging) {
                    const approverNames = resolveApproverNames(tx._badging.roleId, tx._approverIds);

                    // Improved Approver Check:
                    // 1. If strict approver IDs exist, YOU must be in that list (unless Admin).
                    // 2. Fallback to Role check if no IDs provided.
                    let isApprover;
                    if (perms.drugAdmin) {
                        isApprover = true;
                    } else if (tx._approverIds && tx._approverIds.length > 0) {
                        isApprover = tx._approverIds.includes(state.currentUser?.uid);
                    } else {
                        // Legacy/Fallback: Check if user holds the required role
                        isApprover = userRoleIds.includes(tx._badging.roleId);
                    }
                    // For Real Transactions (Soft Block), the approval ID is NOT the tx.id.
                    // We must find it from the approvalMap.
                    // If pseudo, tx.id IS the approval id.
                    const linkedApproval = approvalMap[tx.id];
                    const linkedApprovalId = linkedApproval ? linkedApproval.id : (tx._isPseudo ? tx.id : null);

                    const cursorClass = (isApprover && linkedApprovalId) ? 'cursor-pointer hover:opacity-80 js-approval-badge' : 'cursor-default';


                    if (tx._badging.type === 'hard') {
                        // Orange Badge (Hard Block)
                        statusHtml = `
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap ${cursorClass}"
                                  title="${(isApprover && linkedApprovalId) ? 'Click to Review' : `Waiting for: ${approverNames}`}"
                                  ${(isApprover && linkedApprovalId) ? `data-approval-id="${linkedApprovalId}"` : ''}>
                                Pending Approval
                            </span>
                            <div class="text-[10px] text-gray-400 mt-1 text-left ml-1 whitespace-normal">Approvers: ${approverNames}</div>
                        `;
                        rowHighlightClass = 'bg-orange-50/30'; // Very subtle tint
                    } else if (tx._badging.type === 'soft') {
                        // Blue Badge (Soft Block)
                        statusHtml = `
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap ${cursorClass}"
                                  title="${(isApprover && linkedApprovalId) ? 'Click to Finalise' : `Waiting for: ${approverNames}`}"
                                  ${(isApprover && linkedApprovalId) ? `data-approval-id="${linkedApprovalId}"` : ''}>
                                Pending Finalisation
                            </span>
                             <div class="text-[10px] text-gray-400 mt-1 text-left ml-1 whitespace-normal">Approvers: ${approverNames}</div>
                        `;
                    }
                }

                // Apply Strikethrough for Voided Transactions
                const isVoided = tx.isVoided === true;
                // Darker grey for voided rows (bg-gray-100)
                const rowClass = `${isVoided ? 'bg-gray-100 text-gray-400' : 'hover:bg-gray-50'} ${rowHighlightClass}`;
                const textClass = isVoided ? 'line-through decoration-gray-400 text-gray-400' : '';

                const isFocusedHistoryRow = focusedHistoryId === tx.id;
                const focusClass = isFocusedHistoryRow ? DRUG_HISTORY_FOCUS_CLASS_STRING : '';
                    tableHtml += `<tr class="${rowClass} ${focusClass}" data-history-id="${tx.id}">`;

                visibleColumns.forEach(key => {
                    let cellContent = '';
                    let cellClass = 'px-4 py-4 text-sm';

                    if (key === 'transactionDate') {
                        cellContent = formatDate(tx.transactionDate);
                        cellClass += ` whitespace-nowrap ${isVoided ? 'text-gray-400' : 'text-gray-500'}`;
                    } else if (key === 'type') {
                        const typeLabel = tx.type === 'restock' ? 'Restock' : tx.type;
                        cellContent = sanitizeHTML(typeLabel);
                        cellClass += ` whitespace-nowrap font-medium capitalize ${textClass} ${isVoided ? '' : 'text-gray-900'}`;
                    } else if (key === 'drug') {
                        cellContent = formatTransactionDrugs(tx);
                        cellClass += ` ${textClass} ${isVoided ? '' : 'text-gray-900'}`;
                    } else if (key === 'location') {
                        cellContent = formatTransactionLocations(tx);
                        cellClass += ` ${textClass} ${isVoided ? '' : 'text-gray-500'}`;
                    } else if (key === 'quantity') {
                        cellContent = quantitiesHtml;
                        if (isVoided) {cellClass += ' opacity-50 grayscale';}
                    } else if (key === 'balance') {
                        cellContent = formatTransactionBalances(tx);
                    } else if (key === 'notes') {
                        cellContent = notesHtml;
                        cellClass += ` ${isVoided ? 'text-gray-400' : 'text-gray-500'}`;
                        // Max-width is handled by column header style, but add overflow handling
                        if (!columnWidths.notes) {
                            cellClass += ' max-w-[200px]';
                        }
                    } else if (key === 'performedBy') {
                        cellContent = userName;
                        cellClass += ` whitespace-nowrap ${isVoided ? 'text-gray-400' : 'text-gray-500'}`;
                    } else if (key === 'status') {
                        cellContent = statusHtml;
                        cellClass += ' whitespace-nowrap';
                    } else if (key === 'actions') {
                        // Use original transaction ID for void button (not split display ID)
                        const txId = tx._originalId || tx.id;

                        // Strict Void Permission Check
                        // 1. Can user void anything?
                        const canVoidAny = isAdmin || !!perms.drugAdmin || !!perms.voidAnyTransaction;
                        // "voidTransaction" is now "Void Own/Approved"
                        const canVoidTarget = !!perms.voidTransaction;

                        // 2. Is it their own OR approved transaction?
                        const uid = state.currentUser?.uid;
                        const isTargetTx = tx.performedBy === uid || tx.approvedBy === uid;

                        // 3. Final Logic:
                        // - Admin/Superuser: Can void everything
                        // - Permission Holder: Can void OWN/APPROVED only
                        const hasPermissionToVoid = canVoidAny || (canVoidTarget && isTargetTx);

                        // Check if transaction can be voided (not after audit start, and NOT ALREADY VOIDED/IS A VOID)
                        const isNotVoidedOrVoidType = !tx.isVoided && tx.type !== 'void';
                        // Reuse the existing helper for audit lock checks, but overlay our strict permission logic
                        const isSystemEditable = canVoidTransaction(tx); // Checks audit locks, etc.
                        const isMaintenanceLocked = isDrugsUiLocked();

                        if ((tx.status === 'approved' || tx.status === 'automatic_approval') && isNotVoidedOrVoidType && isSystemEditable && hasPermissionToVoid) {
                            cellContent = `<button data-tx-id="${txId}" class="btn-void-tx text-red-600 hover:text-red-900 ${isMaintenanceLocked ? 'opacity-50 cursor-not-allowed' : ''}" ${isMaintenanceLocked ? 'disabled' : ''} title="${isMaintenanceLocked ? 'Drugs system locked for maintenance' : 'Void'}"><span class="material-symbols-outlined text-lg">block</span></button>`;
                        } else {
                            cellContent = '';
                        }

                        cellClass += ' whitespace-nowrap text-right font-medium';
                    }

                    // Apply overflow handling for notes column if no custom width set
                    const cellStyle = (key === 'notes' && !columnWidths.notes) ? 'style="overflow: hidden; text-overflow: ellipsis;"' : '';
                    tableHtml += `<td class="${cellClass}" ${cellStyle}>${cellContent}</td>`;
                });

                tableHtml += `</tr>`;
            }
        });
    }

    tableHtml += `
                </tbody>
            </table>
            </div>
            ${renderDrugHistoryFilterMenu({
                settings,
                columns: visibleColumnDefinitions,
                rows: tableFilterSourceRows,
            })}
        </div>`;

    // Pagination / Load More / Loading State
    if (state.drugHistoryPagination?.hasMore) {
        const requiredVisible = state.drugHistoryPagination.minVisibleItems || 50;
        if (filteredTransactions.length < requiredVisible) {
            // Auto-fetching mode
            tableHtml += `
            <div class="py-6 text-center bg-gray-50 border-t border-gray-100">
                <div class="flex flex-col items-center justify-center animate-pulse">
                    <span class="material-symbols-outlined text-blue-500 mb-2 text-2xl spin-slow">sync</span>
                    <span class="text-sm text-gray-600 font-medium">Scanning older records...</span>
                    <span class="text-xs text-gray-400 mt-1">Found ${filteredTransactions.length} matching rows so far</span>
                </div>
            </div>
            `;
        } else {
            // Manual load more
            tableHtml += `
            <div class="mt-4 text-center pb-8">
                <button id="btn-load-more" class="${DRUG_SECONDARY_BUTTON_CLASSES}">
                    Load More Transactions
                </button>
            </div>
            `;
        }
    } else if (filteredTransactions.length === 0) {
        tableHtml += `
            <div class="py-12 text-center">
                <span class="material-symbols-outlined text-gray-300 text-4xl mb-2">search_off</span>
                <p class="text-gray-500">No transactions found matching the current filters.</p>
            </div>
        `;
    }

    container.innerHTML = headerHtml + tableHtml;
    bindDrugNativeFieldSurface(container.querySelector('#column-toggle-dropdown-drugs-history'));
    if (focusedHistoryId) {
        requestAnimationFrame(() => {
            const focusedRow = container.querySelector(`[data-history-id="${focusedHistoryId}"]`);
            if (focusedRow) {
                focusedRow.scrollIntoView({ block: 'center', behavior: 'smooth' });
                window.setTimeout(() => {
                    focusedRow.classList.remove(...DRUG_HISTORY_FOCUS_CLASSES);
                    if (state.drugHistoryFocusId === focusedHistoryId) {
                        state.drugHistoryFocusId = null;
                    }
                }, 1800);
            } else if (state.drugHistoryFocusId === focusedHistoryId) {
                state.drugHistoryFocusId = null;
            }
        });
    }

    // Add event listeners
    setupDrugHistoryTableListeners(container, {
        columns: visibleColumnDefinitions,
        rows: tableFilterSourceRows,
    });
}

/**
 * Sets up interaction listeners for the drugs history table (sorting, filtering, column visibility, resizing, reordering).
 * @param {HTMLElement} container - Container element.
 * @param {object} tableFilterOptions - Table filter binding options.
 * @param {Array<object>} tableFilterOptions.columns - Visible column definitions.
 * @param {Array<object>} tableFilterOptions.rows - Source filter rows.
 */
function setupDrugHistoryTableListeners(container, tableFilterOptions = {}) {
    if (!container) {return;}

    const settings = state.viewSettings?.drugsHistory;
    if (!settings) {return;}

    const tableWrapper = container.querySelector('[data-drug-history-table-wrapper]');
    if (tableWrapper) {
        bindDrugHistoryTableFilterControls({
            wrapper: tableWrapper,
            settings,
            rows: tableFilterOptions.rows || [],
            columns: tableFilterOptions.columns || [],
            render: () => renderDrugHistoryTable(container),
        });
    }

    let draggedColumnKey = null;
    const resizeState = {
        thElm: null,
        startOffset: 0,
        startWidth: 0,
        resizeColumnKey: null,
        boundMouseMove: null,
        boundMouseUp: null
    };

    // Column toggle button
    const toggleBtn = document.getElementById('column-toggle-btn-drugs-history');
    const toggleDropdown = document.getElementById('column-toggle-dropdown-drugs-history');
    if (toggleBtn && toggleDropdown) {
        const setColumnToggleOpenState = (isOpen) => {
            toggleBtn.setAttribute('aria-expanded', String(isOpen));
            toggleBtn.dataset.menuOpen = String(isOpen);
            toggleBtn.style.color = isOpen ? 'var(--dfc-brand-accent)' : '';
        };

        toggleBtn.addEventListener('pointerenter', () => {
            if (toggleBtn.dataset.menuOpen === 'true') {return;}
            toggleBtn.style.color = 'var(--dfc-brand-primary)';
        });

        toggleBtn.addEventListener('pointerleave', () => {
            if (toggleBtn.dataset.menuOpen === 'true') {return;}
            toggleBtn.style.color = '';
        });

        toggleBtn.onclick = (e) => {
            e.stopPropagation();
            toggleDropdown.classList.toggle('hidden');
            const isOpen = !toggleDropdown.classList.contains('hidden');
            setColumnToggleOpenState(isOpen);
        };

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!toggleBtn.contains(e.target) && !toggleDropdown.contains(e.target)) {
                toggleDropdown.classList.add('hidden');
                setColumnToggleOpenState(false);
            }
        });
    }

    // Column visibility checkboxes & Filter Type Change
    container.onchange = (e) => {
        // Column Visibility
        if (e.target.matches('.column-toggle-checkbox-drugs-history')) {
            const key = e.target.dataset.key;
            if (e.target.checked) {
                if (!settings.visibleColumns.includes(key)) {
                    settings.visibleColumns.push(key);
                }
            } else {
                settings.visibleColumns = settings.visibleColumns.filter(c => c !== key);
            }
            saveViewSettings();
            renderDrugHistoryTable(container);
        }
    };

    const toggleAuditDetails = (auditId) => {
        if (!auditId) {return;}
        const row = container.querySelector(`.audit-summary-row[data-audit-id="${auditId}"]`);
        const detailsRow = container.querySelector(`.audit-details-row[data-audit-id="${auditId}"]`);
        const toggleButton = row?.querySelector('.btn-toggle-audit-details');
        const expandIcon = row?.querySelector('.audit-expand-icon');
        if (!detailsRow) {return;}

        detailsRow.classList.toggle('hidden');
        const isExpanded = !detailsRow.classList.contains('hidden');
        toggleButton?.setAttribute('aria-expanded', String(isExpanded));
        if (expandIcon) {
            expandIcon.textContent = isExpanded ? 'expand_less' : 'expand_more';
        }
    };

    // Click handlers (sorting, filtering, loading)
    container.onclick = (e) => {
        // Load More button
        if (e.target.closest('#btn-load-more')) {
            state.drugHistoryPagination.minVisibleItems = (state.drugHistoryPagination.minVisibleItems || 50) + 50;
            renderDrugHistoryTable(container); // Re-render to trigger recursive fetch
            return;
        }

        // Void transaction button
        if (e.target.closest('.btn-void-tx')) {
            e.stopPropagation();
            const button = e.target.closest('.btn-void-tx');
            const txId = button.dataset.txId;
            if (txId) {
                handleVoidTransaction(txId);
            }
        }
        // Print audit button (in summary row) - check before row click
        else if (e.target.closest('.btn-print-audit')) {
            e.stopPropagation();
            const button = e.target.closest('.btn-print-audit');
            const auditId = button.dataset.auditId;
            if (auditId) {
                handlePrintAudit(auditId);
            }
        }
        // Print audit button (in details)
        else if (e.target.closest('.btn-print-audit-details')) {
            e.stopPropagation();
            const button = e.target.closest('.btn-print-audit-details');
            const auditId = button.dataset.auditId;
            if (auditId) {
                handlePrintAudit(auditId);
            }
        }
        // Audit details toggle button
        else if (e.target.closest('.btn-toggle-audit-details')) {
            e.stopPropagation();
            const button = e.target.closest('.btn-toggle-audit-details');
            toggleAuditDetails(button.dataset.auditId);
        }
        // Audit summary row click (expand/collapse) - check after button clicks
        else if (e.target.closest('.audit-summary-row')) {
            e.stopPropagation();
            const row = e.target.closest('.audit-summary-row');
            toggleAuditDetails(row.dataset.auditId);
        }
        // Approval Badge click
        else if (e.target.closest('.js-approval-badge')) {
            e.stopPropagation();
            const badge = e.target.closest('.js-approval-badge');
            const approvalId = badge.dataset.approvalId;

            if (approvalId) {
                // Find approval data
                const pending = appData.allPendingApprovals || [];
                const allApprovals = appData.drugApprovals || [];

                const approvalData = pending.find(a => a.id === approvalId) || allApprovals.find(a => a.id === approvalId);

                if (approvalData) {
                    renderApprovalModal(approvalId, approvalData, () => {
                        // refreshing the table
                        renderDrugHistoryTable(container);
                    });
                } else {
                    console.error("Approval data not found for ID:", approvalId);
                }
            }
        }
    };

    // Drag handlers (column reordering)
    container.ondragstart = (e) => {
        if (e.target.matches('.sortable')) {
            draggedColumnKey = e.target.dataset.key;
            e.target.classList.add('dragging-header');
            try {
                e.dataTransfer.setData('text/plain', draggedColumnKey);
            } catch (err) {
                console.warn("Could not set drag data", err);
            }
            e.dataTransfer.effectAllowed = 'move';
        }
    };

    container.ondragend = (e) => {
        if (e.target.matches('.sortable')) {
            e.target.classList.remove('dragging-header');
            container.querySelectorAll('.drag-over-header').forEach(el => el.classList.remove('drag-over-header'));
            draggedColumnKey = null;
        }
    };

    container.ondragover = (e) => {
        const targetHeader = e.target.closest('.sortable');
        if (targetHeader && draggedColumnKey && targetHeader.dataset.key !== draggedColumnKey) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            container.querySelectorAll('.drag-over-header').forEach(el => {
                if (el !== targetHeader) {el.classList.remove('drag-over-header');}
            });
            targetHeader.classList.add('drag-over-header');
        } else if (targetHeader && draggedColumnKey && targetHeader.dataset.key === draggedColumnKey) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'none';
            targetHeader.classList.remove('drag-over-header');
        }
    };

    container.ondragleave = (e) => {
        if (e.target.matches('.sortable')) {
            e.target.classList.remove('drag-over-header');
        }
    };

    container.ondrop = (e) => {
        e.preventDefault();
        const targetHeader = e.target.closest('.sortable');
        const targetKey = targetHeader?.dataset.key;

        if (draggedColumnKey && targetKey && draggedColumnKey !== targetKey && settings.visibleColumns) {
            const cols = settings.visibleColumns;
            const draggedIndex = cols.indexOf(draggedColumnKey);
            const targetIndex = cols.indexOf(targetKey);

            if (draggedIndex > -1 && targetIndex > -1) {
                const [draggedItem] = cols.splice(draggedIndex, 1);
                cols.splice(targetIndex, 0, draggedItem);
                saveViewSettings();
                renderDrugHistoryTable(container);
            }
        }
    };

    // Resize handlers
    const onResizeMouseMove = (e) => {
        if (!resizeState.thElm) {return;}
        e.preventDefault();
        const dx = e.clientX - resizeState.startOffset;
        const newWidth = resizeState.startWidth + dx;
        if (newWidth > 50) {
            resizeState.thElm.style.width = `${newWidth}px`;
        }
    };

    const onResizeMouseUp = (e) => {
        if (!resizeState.thElm || !resizeState.resizeColumnKey) {return;}
        e.preventDefault();
        const newWidth = resizeState.thElm.offsetWidth;
        if (!settings.columnWidths) {settings.columnWidths = {};}
        settings.columnWidths[resizeState.resizeColumnKey] = newWidth;
        saveViewSettings();

        // Cleanup
        document.removeEventListener('mousemove', resizeState.boundMouseMove);
        document.removeEventListener('mouseup', resizeState.boundMouseUp);
        resizeState.thElm = null;
        resizeState.resizeColumnKey = null;
        resizeState.boundMouseMove = null;
        resizeState.boundMouseUp = null;
    };

    container.onmousedown = (e) => {
        const resizer = e.target.closest('.table-resizer');
        if (resizer) {
            e.preventDefault();
            const th = resizer.closest('th');
            if (th) {
                resizeState.thElm = th;
                resizeState.startOffset = e.clientX;
                resizeState.startWidth = th.offsetWidth;
                resizeState.resizeColumnKey = th.dataset.key;

                resizeState.boundMouseMove = onResizeMouseMove;
                resizeState.boundMouseUp = onResizeMouseUp;

                document.addEventListener('mousemove', resizeState.boundMouseMove);
                document.addEventListener('mouseup', resizeState.boundMouseUp);
            }
        }
    };

    // Export button
    const exportBtn = document.getElementById('btn-export');
    if (exportBtn) {
        exportBtn.onclick = () => {
            openExportModal();
        };
    }


    // Load more button
    const loadMoreBtn = document.getElementById('btn-load-more');
    if (loadMoreBtn) {
        loadMoreBtn.onclick = async () => {
            if (state.drugHistoryPagination?.lastDoc) {
                await loadMoreDrugTransactions(state.drugHistoryPagination.lastDoc);
                renderDrugHistoryTable(container);
            }
        };
    }
}

// --- AUDIT CONSOLE ---

/**
 * Renders the audit console view.
 * @param {HTMLElement} container - Container element to render into.
 */
export function renderAuditConsole(container) {
    if (!container) {
        console.error('renderAuditConsole: container not provided');
        return;
    }
    applyDrugControlSurface(container, appData);
    container.innerHTML = '';

    const activeAudit = appData.activeAudit;
    const inventory = appData.drugInventory || [];
    const locations = sortLocationsByOrder(appData.config?.drugs?.locations || []);

    // Header
    const headerHtml = `
        <div class="flex justify-between items-center mb-6">
            <h1 class="text-2xl font-bold text-gray-800">
                ${activeAudit ? 'Active Audit In Progress' : 'Start Stock Audit'}
            </h1>
        </div>
    `;

    // START PERMISSION CHECK
    const profile = state.currentUserProfile;
    const perms = profile?.drugPermissions || {};
    const isAdmin = profile?.role === 'admin';
    const canStart = isAdmin || perms.drugAdmin === true || perms.startAudit === true;
    const canDelete = isAdmin || perms.drugAdmin === true || perms.deleteAudit === true;
    const canFinalize = isAdmin || perms.drugAdmin === true || perms.finalizeAudit === true;
    const isMaintenanceLocked = isDrugsUiLocked();
    // END PERMISSION CHECK

    if (!activeAudit) {
        // Initial Render: Loading State
        container.innerHTML = headerHtml + `
            <div id="audit-start-container" class="text-center py-12 bg-white rounded-lg shadow">
                 <span class="material-symbols-outlined text-4xl text-gray-400 animate-spin mb-4">refresh</span>
                 <p class="text-gray-500">Checking audit status...</p>
            </div>
        `;

        // Async Status Check
        checkPendingApprovals().then((result) => {
            const containerDiv = document.getElementById('audit-start-container');
            if (!containerDiv) {return;}

            // Check if there is specifically a 'drug_audit_start' approval pending
            const pendingStart = result.pendingApprovals?.find(a => a.workflowType === 'drug_audit_start');

            if (pendingStart) {
                // RENDER: Audit Requested State
                // Resolve Requester Name
                const requesterObj = (appData.users || []).find(u => u.id === pendingStart.requesterId);
                const requesterName = requesterObj ? sanitizeHTML(requesterObj.name) : 'Unknown User';

                // Resolve Date
                let dateStr = 'Unknown Date';
                try {
                    dateStr = formatDate(pendingStart.createdAt);
                } catch (e) { void e; }

                // Resolve Approver
                let approverText = 'Pending Approval';
                // Check if current user is an approver for this request
                const currentUserId = state.currentUser?.uid;
                let isApprover = false;

                if (pendingStart.approverIds && pendingStart.approverIds.length > 0) {
                    const names = pendingStart.approverIds.map(uid => {
                        const u = (appData.users || []).find(user => user.id === uid);
                        return u ? sanitizeHTML(u.name) : 'Unknown User';
                    });
                    approverText = names.join(', ');
                    if (currentUserId && pendingStart.approverIds.includes(currentUserId)) {
                        isApprover = true;
                    }
                } else if (pendingStart.currentApproverRole) {
                    const roleObj = (appData.drugRoles || []).find(r => r.id === pendingStart.currentApproverRole);
                    approverText = roleObj ? roleObj.name : pendingStart.currentApproverRole;
                    // Check role based approval (less precise without backend check but good for UI hint)
                    // We assume specific IDs are always populated in 'approverIds' by backend resolveApprovers
                }

                // Global Admin override
                if (state.currentUser?.drugPermissions?.drugAdmin) {
                    isApprover = true;
                }

                let actionButton = `
                    <button disabled class="${DRUG_SECONDARY_BUTTON_CLASSES}">
                        Start New Audit
                    </button>
                `;

                if (isApprover) {
                    actionButton = `
                        <button id="btn-review-audit" class="${DRUG_PRIMARY_BUTTON_CLASSES} mx-auto">
                            <span class="material-symbols-outlined">rate_review</span>
                            Review Request
                        </button>
                    `;
                }

                containerDiv.innerHTML = `
                    <span class="material-symbols-outlined text-6xl text-gray-900 mb-4">pending_actions</span>
                    <h3 class="text-lg font-medium text-gray-900">Audit Requested</h3>
                    <p class="text-gray-500 mb-6 max-w-md mx-auto">
                        <span class="font-semibold text-gray-900">${requesterName}</span> requested an audit on <span class="font-medium">${dateStr}</span>.
                        <br>It is pending approval from <span class="font-semibold text-gray-900">${approverText}</span>.
                    </p>
                    ${actionButton}
                `;

                // Attach listener for review button
                const reviewBtn = document.getElementById('btn-review-audit');
                if (reviewBtn) {
                    reviewBtn.onclick = () => {
                        renderApprovalModal(pendingStart.id, pendingStart, () => {
                            // Refresh console after action
                            renderAuditConsole(container);
                        });
                    };
                }

            } else {
                // RENDER: Ready to Audit (Standard State)
                const maintenanceLockedForStart = isDrugsUiLocked();
                containerDiv.innerHTML = `
                    <span class="material-symbols-outlined text-6xl text-gray-300 mb-4">fact_check</span>
                    <h3 class="text-lg font-medium text-gray-900">Ready to Audit</h3>
                    <p class="text-gray-500 mb-6">Starting an audit will snapshot current stock levels.</p>
                    ${canStart ? `
                    <button id="btn-start-audit" class="${DRUG_PRIMARY_BUTTON_CLASSES}" ${maintenanceLockedForStart ? 'disabled title="Drugs system locked for maintenance"' : ''}>
                        Start New Audit
                    </button>` : `<p class="text-red-500 font-medium bg-red-50 p-3 rounded inline-block">You do not have permission to start an audit.</p>`
                    }
                `;

                // Re-attach listener
                const startBtn = document.getElementById('btn-start-audit');
                if (startBtn && !maintenanceLockedForStart) {
                    startBtn.onclick = handleStartAuditClick;
                }
            }
        }).catch(err => {
            console.error("Error checking audit status:", err);
            // Fallback to allow start (or show error) - defaulting to standard view but logging error
            const containerDiv = document.getElementById('audit-start-container');
            if (containerDiv) {
                containerDiv.innerHTML = `
                    <p class="text-red-500 mb-4">Error checking status. Please refresh.</p>
                     <button onclick="renderAuditConsole(document.getElementById('drugs-view'))" class="px-4 py-2 bg-gray-200 rounded">Retry</button>
                 `;
            }
        });

        // Abstracted handler for re-use
        const handleStartAuditClick = async () => {
            const startBtn = document.getElementById('btn-start-audit');
            if (!startBtn) {return;}
            // PRE-FLIGHT CHECK: Check for pending approvals
            try {
                startBtn.disabled = true;
                startBtn.innerHTML = '<span class="material-symbols-outlined animate-spin text-sm">refresh</span> Checking...';

                const checkResult = await checkPendingApprovals();
                if (checkResult.pendingApprovals && checkResult.pendingApprovals.length > 0) {
                    // Found blocking approvals
                    // Sort Newest First (Desc) based on createdAt
                    checkResult.pendingApprovals.sort((a, b) => {
                        const dateA = new Date(a.createdAt?._seconds ? a.createdAt._seconds * 1000 : a.createdAt);
                        const dateB = new Date(b.createdAt?._seconds ? b.createdAt._seconds * 1000 : b.createdAt);
                        return dateB - dateA;
                    });

                    const listHtml = checkResult.pendingApprovals.map(a => {
                        // 'a.payload' is the canonical approval transaction object.
                        const tx = a.payload || {};

                        // Initialize Display Variables
                        let drugName = 'Unknown Drug';
                        let locationName = 'Unknown Location';

                        // 1. Resolve Drug & Location
                        let item = null;
                        if (Array.isArray(tx.payload) && tx.payload.length > 0) {
                            item = tx.payload[0];
                        }

                        if (item) {
                            const drugObj = (appData.drugInventory || []).find(d => d.id === item.drugId);
                            if (drugObj) {drugName = drugObj.name;}

                            const locId = item.location || item.fromLocation || item.toLocation;
                            const locObj = (appData.config?.drugs?.locations || []).find(l => l.id === locId);
                            if (locObj) {locationName = locObj.name;}
                        }

                        // 2. Resolve Type
                        const type = tx.type ? (tx.type.charAt(0).toUpperCase() + tx.type.slice(1)) : 'Transaction';

                        // 3. Resolve Date (Prefer transactionDate)
                        let dateStr = 'Unknown Date';
                        try {
                            const dateVal = tx.transactionDate || a.createdAt;
                            dateStr = formatDate(dateVal);
                        } catch (e) { void e; } // Keep default

                        // 4. Resolve Approver
                        // Priority: Specific Users (approverIds) > Role Name > "Pending"
                        let approverText = 'Pending Approval';

                        if (a.approverIds && Array.isArray(a.approverIds) && a.approverIds.length > 0) {
                            // Map UIDs to Names
                            const names = a.approverIds.map(uid => {
                                const u = (appData.users || []).find(user => user.id === uid);
                                return u ? sanitizeHTML(u.name) : 'Unknown User';
                            });
                            approverText = `Waiting for ${names.join(', ')}`;
                        } else if (a.currentApproverRole) {
                            // Fallback to Role
                            const roleObj = (appData.drugRoles || []).find(r => r.id === a.currentApproverRole);
                            approverText = roleObj ? `Waiting for ${roleObj.name}` : `Waiting for ${sanitizeHTML(a.currentApproverRole)}`;
                        }

                        // Condensed 2-Line Layout (No colors, no clock icon)
                        return `<li class="mb-2 p-3 bg-white rounded border border-gray-200 shadow-sm flex flex-col justify-between">
                                <div class="text-sm text-gray-900 mb-1">
                                    <span class="font-bold">${sanitizeHTML(type)}:</span>
                                    ${sanitizeHTML(drugName)} from ${sanitizeHTML(locationName)} on <span>${dateStr}</span>
                                </div>
                                <div class="text-xs text-gray-500 font-medium">
                                    ${sanitizeHTML(approverText)}
                                </div>
                            </li>`;
                    }).join('');

                    openConfirmationModal(
                        'Audit Blocked',
                        `<div class="text-left">
                                <p class="mb-3 text-gray-700 text-base">You cannot start an audit until the following items are resolved:</p>
                                <ul class="list-none space-y-2 bg-gray-50 p-4 rounded border border-gray-200 h-96 overflow-y-auto">
                                    ${listHtml}
                                </ul>
                             </div>`,
                        () => { },
                        false,
                        'OK'
                    );

                    // HACK: Increase modal width for this specific instance if needed,
                    // but usually "w-full max-w-sm" (from index.html default) is too small.
                    // We need a wider modal.
                    // Let's dynamically adjust the modal classes after opening.
                    const modalEl = document.getElementById('confirm-modal');
                    if (modalEl) {
                        const inner = modalEl.querySelector('div'); // The white box
                        if (inner) {
                            inner.classList.remove('max-w-sm');
                            inner.classList.add('max-w-2xl'); // Make is wider (like form modal)
                        }
                    }
                    startBtn.disabled = false;
                    startBtn.textContent = 'Start New Audit';
                    return;
                }
            } catch (err) {
                console.error("Pre-flight check failed:", err);
                // Decide validation strigency. For now, we block on error to be safe or maybe warn?
                // User requirement: "Audit Pre-Flight Checks".
                // If check fails, maybe warn but allow?
                // "You must successfully deploy backend functions" - implies robust system.
                // Let's warn but allow proceed if it's just a network error, but typically we want to be strict.
                // However, blocking on network error is annoying.
                // Let's show error modal.
                openConfirmationModal(
                    'Pre-Flight Check Failed',
                    `Could not verify pending approvals: ${err.message}. Please try again.`,
                    () => { },
                    false,
                    'OK'
                );
                startBtn.disabled = false;
                startBtn.textContent = 'Start New Audit';
                return;
            }

            startAudit().then((result) => {
                if (result && result.success) {
                    // Reset modal width just in case
                    const modalEl = document.getElementById('confirm-modal');
                    if (modalEl) {
                        const inner = modalEl.querySelector('div');
                        if (inner) {
                            inner.classList.add('max-w-sm');
                            inner.classList.remove('max-w-2xl');
                        }
                    }

                    renderSubmissionFeedback(result, 'audit_start');
                }
                // Reset button state (though startAudit usually triggers re-render)
                startBtn.disabled = false;
                startBtn.textContent = 'Start New Audit';
            }).catch((err) => {
                console.error('Failed to start audit:', err);
                openConfirmationModal(
                    'Error',
                    'Failed to start audit. Please try again.',
                    () => { },
                    false,
                    'OK',
                );
                startBtn.disabled = false;
                startBtn.textContent = 'Start New Audit';
            });
        };
        return;
    }

    // Active Audit Table - Organized by Location
    let auditHtml = `
        <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drug</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Expected</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actual Count</th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Discrepancy</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/4">Notes</th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
    `;

    // Calculate historical presence for each drug in each location
    // This allows us to show 0-balance rows for drugs that "belong" or have ever been in a location
    const historicalPresence = new Set();
    const allTransactions = appData.drugTransactions || [];

    allTransactions.forEach(tx => {
        // Payload items (corrections, relocations, bulk actions)
        if (tx.payload && Array.isArray(tx.payload)) {
            tx.payload.forEach(item => {
                if (item.drugId && item.location) {
                    historicalPresence.add(`${item.drugId}_${item.location}`);
                }
            });
        }
    });

    const sortedInventory = sortDrugsByOrder(
        inventory.filter((d) => d.isActive !== false)
    );
    const sortedLocations = sortLocationsByOrder(locations);

    // Group by location - show ALL active locations, even if no drugs
    sortedLocations.forEach((loc, _locIndex) => {
        let locationRows = '';

        sortedInventory.forEach((drug) => {
            const key = `${drug.id}_${loc.id}`;
            const expected = activeAudit.snapshot?.[key] || 0;
            const savedActual = activeAudit.actuals?.[key];
            const savedNote = activeAudit.discrepancyNotes?.[key] || '';
            const actualVal = savedActual !== undefined ? savedActual : '';
            const isHistoricallyPresent = historicalPresence.has(key);

            // Show rows where expected > 0 OR actual is entered OR historically present
            // This ensures we audit drugs that are "supposed" to be there (based on history) even if currently 0
            if (expected > 0 || savedActual !== undefined || actualVal !== '' || isHistoricallyPresent) {
                const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown') : 'Unknown';
                const discrepancy = savedActual !== undefined ? (savedActual - expected) : null;
                const hasDiscrepancy = discrepancy !== null && discrepancy !== 0;

                // Hide note input by default if no discrepancy and no note content
                const noteHidden = !hasDiscrepancy && (!savedNote || savedNote.trim() === '');
                const noteRequired = hasDiscrepancy ? 'required' : '';
                const noteClass = `audit-note drug-audit-note-input ${DRUG_MODAL_AUTO_TEXTAREA_CLASSES} ${noteHidden ? 'hidden' : ''}`;

                locationRows += `
                    <tr class="hover:bg-gray-50">
                        <td class="px-6 py-4 text-sm text-gray-500"></td>
                        <td class="px-6 py-4 text-sm font-medium text-gray-900">${sanitizeHTML(drugDisplayName)}</td>
                        <td class="px-6 py-4 text-sm text-center text-gray-500">${expected}</td>
                        <td class="px-6 py-4 text-center align-middle">
                            <input type="number" step="any" class="audit-input drug-compact-number-input ${DRUG_MODAL_INPUT_CLASSES} text-center" style="width: 5rem;"
                                data-key="${key}" data-expected="${expected}" value="${actualVal}">
                        </td>
                        <td class="px-6 py-4 text-center align-middle text-sm font-bold" id="disc-${key}">
                            ${savedActual !== undefined ? formatDiscrepancy(discrepancy) : '-'}
                        </td>
                        <td class="px-6 py-4 align-middle">
                            <textarea rows="1" class="${noteClass}" ${noteRequired}
                                data-auto-grow="true" data-key="${key}" placeholder="${hasDiscrepancy ? 'Add a reason' : 'Add a note'}">${sanitizeHTML(savedNote)}</textarea>
                        </td>
                    </tr>
                `;
            }
        });

        // Always show location header row (even if no drugs - shows empty location)
        auditHtml += `
            <tr class="bg-gray-100 border-t-2 border-gray-300">
                <td class="px-6 py-3 text-sm font-bold text-gray-900" colspan="6">
                    ${sanitizeHTML(loc.name)}
                </td>
            </tr>
        `;
        // Add location rows (will be empty if no drugs, but location header still shows)
        if (locationRows) {
            auditHtml += locationRows;
        } else {
            // Add empty row with input field to confirm location has 0 drugs
            auditHtml += `
                <tr class="hover:bg-gray-50">
                    <td class="px-6 py-4 text-sm text-gray-500"></td>
                    <td class="px-6 py-4 text-sm text-gray-400 italic">No drugs in this location</td>
                    <td class="px-6 py-4 text-sm text-center text-gray-500">0</td>
                    <td class="px-6 py-4 text-center align-middle">
                        <input type="number" step="any" class="audit-input drug-compact-number-input ${DRUG_MODAL_INPUT_CLASSES} text-center" style="width: 5rem;"
                            data-key="empty_${loc.id}" data-expected="0" value="0" min="0">
                    </td>
                    <td class="px-6 py-4 text-center align-middle text-sm font-bold" id="disc-empty_${loc.id}">
                        <span class="text-green-600">0</span>
                    </td>
                    <td class="px-6 py-4 align-middle">
                        <textarea rows="1" class="audit-note drug-audit-note-input ${DRUG_MODAL_AUTO_TEXTAREA_CLASSES}"
                            data-auto-grow="true" data-key="empty_${loc.id}" placeholder="Add a note"></textarea>
                    </td>
                </tr>
            `;
        }
    });

    auditHtml += `</tbody></table></div>`;

    // Footer Actions
    auditHtml += `
        <div class="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
            ${canDelete ? `<button id="btn-cancel-audit" class="${DRUG_DANGER_TEXT_BUTTON_CLASSES}" ${isMaintenanceLocked ? 'disabled title="Drugs system locked for maintenance"' : ''}>Delete/Cancel Audit</button>` : '<div></div>'}
            ${canFinalize ? `<button id="btn-finalize-audit" class="${DRUG_APPROVE_BUTTON_CLASSES}" ${isMaintenanceLocked ? 'disabled title="Drugs system locked for maintenance"' : ''}>
                Finalise & Submit
            </button>` : ''}
        </div>
    `;

    container.innerHTML = headerHtml + auditHtml;
    bindDrugNativeFieldSurface(container);

    // Debounce timer for auto-save
    let saveTimer = null;
    const debounceSave = (callback, delay = 1000) => {
        if (saveTimer) {clearTimeout(saveTimer);}
        saveTimer = setTimeout(callback, delay);
    };

    // Auto-save function
    const saveAuditProgress = async () => {
        if (!activeAudit || activeAudit.status !== 'in_progress') {return;}

        const actuals = {};
        const discrepancyNotes = {};

        container.querySelectorAll('.audit-input').forEach((input) => {
            const key = input.dataset.key;
            const actual = parseFloat(input.value);
            if (!isNaN(actual)) {
                actuals[key] = actual;
            }
        });

        container.querySelectorAll('.audit-note').forEach((noteInput) => {
            const key = noteInput.dataset.key;
            const note = noteInput.value.trim();
            if (note) {
                discrepancyNotes[key] = note;
            }
        });

        try {
            await updateAuditProgress(activeAudit.id, actuals, discrepancyNotes);
        } catch (error) {
            console.error('Error auto-saving audit progress:', error);
            // Don't show error to user - auto-save failures are silent
        }
    };

    // Live Note Input Visibility and Requirement
    // Also save on blur (immediate save when user leaves field)
    container.querySelectorAll('.audit-input').forEach((input) => {
        // Input event for visibility logic
        input.addEventListener('input', (e) => {
            const val = parseFloat(e.target.value);
            const expected = parseFloat(e.target.dataset.expected);
            const key = e.target.dataset.key;
            const cell = document.getElementById(`disc-${key}`);
            const noteInput = container.querySelector(`.audit-note[data-key="${key}"]`);

            // Update Discrepancy Display
            if (cell) {
                if (!isNaN(val)) {
                    const diff = val - expected;
                    cell.innerHTML = formatDiscrepancy(diff);
                } else {
                    cell.innerHTML = '-';
                }
            }

            if (noteInput) {
                if (!isNaN(val) && val !== expected) {
                    noteInput.classList.remove('hidden');
                    noteInput.setAttribute('required', 'true');
                    noteInput.placeholder = "Add a reason";
                } else {
                    if (noteInput.value.trim() === '') {
                        noteInput.classList.add('hidden');
                        noteInput.removeAttribute('required');
                        noteInput.placeholder = "Add a note";
                    } else {
                        noteInput.removeAttribute('required');
                        noteInput.classList.remove('hidden');
                        noteInput.placeholder = "Add a note";
                    }
                }
            }

            // Debounced auto-save
            debounceSave(saveAuditProgress);
        });

        // Blur event for auto-save
        input.addEventListener('blur', () => {
            if (saveTimer) {clearTimeout(saveTimer);}
            saveAuditProgress();
        });
    });

    // Auto-save notes on blur
    container.querySelectorAll('.audit-note').forEach((noteInput) => {
        noteInput.addEventListener('blur', () => {
            if (saveTimer) {clearTimeout(saveTimer);}
            saveAuditProgress();
        });
    });

    // Event Listeners
    const cancelBtn = document.getElementById('btn-cancel-audit');
    const finalizeBtn = document.getElementById('btn-finalize-audit');

    if (cancelBtn) {
        cancelBtn.onclick = () => {
            if (isMaintenanceLocked) {return;}
            const modalContent = `
                <div class="space-y-4 px-1">
                    <p class="text-gray-600 dark:text-gray-300">Are you sure you want to cancel this audit? This will discard all progress.</p>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Cancellation</label>
                        <textarea id="cancel-audit-reason" rows="1" class="${DRUG_MODAL_AUTO_TEXTAREA_CLASSES}" data-auto-grow="true" placeholder="Add a reason"></textarea>
                    </div>
                </div>
            `;

            openConfirmationModal(
                'Cancel Audit',
                modalContent,
                () => {
                    const reasonInput = document.getElementById('cancel-audit-reason');
                    const reason = reasonInput ? reasonInput.value.trim() : '';

                    if (!reason) {
                        return requireDrugActionFieldValue(
                            document.getElementById('confirm-modal'),
                            reasonInput,
                            'Reason is required to cancel this audit.',
                        );
                    }

                    const performDelete = (isForce = false) => {
                        deleteActiveAudit(reason, isForce).then((result) => {
                            if (result && result.success) {
                                renderSubmissionFeedback(result, 'audit_cancel');
                            } else if (result && result.code === 'pending_approval_exists') {
                                // Confirmation to Force
                                openConfirmationModal(
                                    'Pending Approvals Detected',
                                    `There are ${result.approvals?.length || 'multiple'} pending approval requests linked to this audit. <br><br>Cancelling this audit will <b>REJECT</b> those requests automatically. <br><br>Do you want to proceed?`,
                                    () => {
                                        performDelete(true);
                                    },
                                    true, // Danger
                                    'Yes, Force Cancel'
                                );
                            }
                        }).catch((err) => {
                            console.error('Failed to delete audit:', err);
                            openConfirmationModal(
                                'Error',
                                'Failed to delete audit. Please try again.',
                                () => { },
                                false,
                                'OK',
                            );
                        });
                    };

                    performDelete(false);
                },
                true, // Danger
                'Cancel Audit',
                null,
                '',
                'Cancel',
                { keepOpenOnFalse: true },
            );
            const confirmModal = document.getElementById('confirm-modal');
            applyDrugControlSurface(confirmModal, appData);
            bindDrugNativeFieldSurface(confirmModal);
        }

    }

    if (finalizeBtn) {
        let isFinalizingAudit = false;

        const setFinalizeButtonState = (isProcessing) => {
            isFinalizingAudit = isProcessing;
            finalizeBtn.disabled = isProcessing;
            finalizeBtn.classList.toggle('opacity-60', isProcessing);
            finalizeBtn.classList.toggle('cursor-not-allowed', isProcessing);
            finalizeBtn.textContent = isProcessing ? 'Finalising...' : 'Finalise & Submit';
        };

        finalizeBtn.onclick = () => {
            if (isMaintenanceLocked) {return;}
            if (isFinalizingAudit) {return;}

            // Validation: Check for required notes for discrepancies
            const visibleRequiredNotes = Array.from(container.querySelectorAll('.audit-note[required]:not(.hidden)'));
            const emptyRequiredNotes = visibleRequiredNotes.filter(input => !input.value.trim());

            if (emptyRequiredNotes.length > 0) {
                openConfirmationModal(
                    'Missing Reasons',
                    'You have discrepancies that require a reason. Please check the red highlighted fields.',
                    () => { }, // No-op callback
                    false,
                    'OK'
                );
                // Highlight them
                emptyRequiredNotes.forEach(input => input.classList.add('ring-2', 'ring-red-500'));
                return;
            }

            const actuals = {};
            const discrepancyNotes = {};
            let missing = false;

            container.querySelectorAll('.audit-input').forEach((input) => {
                if (input.value === '') {missing = true;}
                const key = input.dataset.key;
                const actual = parseFloat(input.value) || 0;
                actuals[key] = actual;
            });

            // Collect all notes
            container.querySelectorAll('.audit-note').forEach((noteInput) => {
                const key = noteInput.dataset.key;
                const note = noteInput.value.trim();
                if (note) {
                    discrepancyNotes[key] = note;
                }
            });

            if (missing) {
                openConfirmationModal(
                    'Missing Data',
                    'Please enter counts for all items. Enter 0 if none found.',
                    () => { },
                    false,
                    'OK',
                );
                return;
            }

            const performFinalize = (isForce = false) => {
                setFinalizeButtonState(true);
                finalizeAudit(activeAudit.id, actuals, discrepancyNotes, isForce).then((result) => {
                    if (result && result.success) {
                        renderSubmissionFeedback(result, 'audit_finalize');
                    } else if (result && result.code === 'pending_approval_exists') {
                        setFinalizeButtonState(false);
                        // Confirmation to Force
                        openConfirmationModal(
                            'Pending Approvals Detected',
                            `There are ${result.approvals?.length || 'multiple'} pending approval requests linked to this audit. <br><br>Finalising this audit will <b>REJECT</b> those requests automatically. <br><br>Do you want to proceed?`,
                            () => {
                                performFinalize(true);
                            },
                            true, // Danger to emphasize importance
                            'Yes, Force Finalise'
                        );
                    } else {
                        setFinalizeButtonState(false);
                    }
                }).catch((err) => {
                    setFinalizeButtonState(false);
                    console.error('Failed to finalise audit:', err);
                    openConfirmationModal(
                        'Error',
                        'Failed to finalise audit. Please try again.',
                        () => { },
                        false,
                        'OK',
                    );
                });
            };

            performFinalize(false);
        }
    }
} // End renderAuditConsole

/**
 * Synchronizes audit console data (actuals and notes) without a full re-render.
 * Prevents focus loss while allowing multi-user collaboration.
 */
export function syncAuditConsoleData() {
    const activeAudit = appData.activeAudit;
    if (!activeAudit || activeAudit.status !== 'in_progress') {return;}

    // Only sync if we are actually looking at the audit console
    if (state.currentView !== 'drugs' || (state.drugsView !== 'audit' && state.drugsView !== 'auditHistory')) {return;}

    const container = document.getElementById('drug-content-container');
    if (!container) {return;}

    // 1. Sync Actuals
    const actuals = activeAudit.actuals || {};
    container.querySelectorAll('.audit-input').forEach((input) => {
        const key = input.dataset.key;
        // If it's the "empty location" placeholder, key is "empty_LOCID"
        const newValue = actuals[key] !== undefined ? actuals[key] : (input.getAttribute('data-key').startsWith('empty_') ? '0' : '');

        // ONLY update if user is not currently focusing on this specific input
        if (document.activeElement !== input) {
            if (input.value !== String(newValue)) {
                input.value = newValue;
                // Trigger the 'input' event to update discrepancy labels and note visibility
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    });

    // 2. Sync Notes
    const notes = activeAudit.discrepancyNotes || {};
    container.querySelectorAll('.audit-note').forEach((noteInput) => {
        const key = noteInput.dataset.key;
        const newValue = notes[key] || '';

        // ONLY update if user is not currently focusing on this specific note field
        if (document.activeElement !== noteInput) {
            if (noteInput.value !== newValue) {
                noteInput.value = newValue;
            }
        }
    });
}

/**
 * Handles drug transaction form submission.
 * Exported for use by the Drugs-owned transaction modal logic.
 * Collects form data, validates, and calls saveDrugTransaction.
 * @param {'use'|'relocate'|'restock'|'correction'|'loss'} type - Transaction type.
 * @param {HTMLFormElement} formElement - The form element.
 */
export async function handleDrugFormSubmit(type, formElement) {
    const submitBtn = formElement.querySelector('button[type="submit"]');
    const originalBtnText = submitBtn ? submitBtn.textContent : 'Submit';

    try {
        // Collect basic form data
        const formData = new FormData(formElement);
        const transactionDate = formData.get('transactionDate');
        const chkBulk = document.getElementById('chk-bulk');
        const isBulk = chkBulk ? chkBulk.checked : (formData.get('isBulk') === 'on');
        const patient = formData.get('patient')?.trim() || '';
        const bulkCode = formData.get('bulkCode')?.trim() || '';
        const provider = formData.get('provider')?.trim() || '';
        const supplier = formData.get('supplier')?.trim() || '';
        const orderNumber = formData.get('orderNumber')?.trim() || '';
        const location = formData.get('location'); // Single location for use, restock, correction, loss
        const fromLocation = formData.get('fromLocation');
        const toLocation = formData.get('toLocation');

        // Validation
        if (!transactionDate) {
            openConfirmationModal(
                'Validation Error',
                'Transaction date is required.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        // Collect items
        const items = [];
        // For use transactions, only get the first row of each 3-row group
        const itemRows = type === 'use'
            ? Array.from(formElement.querySelectorAll('.drug-row')).filter(row => row.querySelector('.input-drug'))
            : formElement.querySelectorAll('.drug-row');

        if (itemRows.length === 0) {
            openConfirmationModal(
                'Validation Error',
                'Please add at least one drug item.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        for (const row of itemRows) {
            let drugSelect = null;
            let qtyInput = null;
            let administeredAmps = null;
            let discardedAmps = null;
            let totalDisplay = null;
            let quantity = null;
            let administered = null;
            let discarded = null;

            // For use transactions, find elements in the 2-row group
            if (type === 'use') {
                // Find the first row of the group (has the drug select and total)
                const firstRow = row;
                drugSelect = firstRow.querySelector('.input-drug');
                totalDisplay = firstRow.querySelector('.input-total');

                // First row has administered inputs
                administeredAmps = firstRow.querySelector('.input-administered-amps');

                // Second row has discarded inputs
                const secondRow = firstRow.nextElementSibling;
                if (secondRow) {
                    discardedAmps = secondRow.querySelector('.input-discarded-amps');
                }

                if (!drugSelect || !administeredAmps || !discardedAmps || !totalDisplay) {continue;}

                const adminValue = parseFloat(administeredAmps.value) || 0;
                const discValue = parseFloat(discardedAmps.value) || 0;
                const total = adminValue + discValue;

                // Capture separate values for payload
                administered = adminValue;
                discarded = discValue;

                // Validate total > 0
                if (total === 0) {
                    openConfirmationModal(
                        'Validation Error',
                        'Total (administered + discarded) must be greater than 0 for each drug.',
                        () => { },
                        false,
                        'OK',
                    );
                    return;
                }

                // Validate whole number
                if (total % 1 !== 0) {
                    openConfirmationModal(
                        'Validation Error',
                        'Total (administered + discarded) must be a whole number for each drug.',
                        () => { },
                        false,
                        'OK',
                    );
                    return;
                }

                // Use total as quantity
                quantity = total;
            } else {
                drugSelect = row.querySelector('.input-drug');
                qtyInput = row.querySelector('.input-qty');
                if (!drugSelect || !qtyInput) {continue;}
                quantity = parseFloat(qtyInput.value);
            }

            if (!drugSelect) {continue;}

            const drugId = drugSelect.value;
            // Use single location from form (for use, restock, correction, loss) or null for relocate
            const _itemLocation = type === 'relocate' ? null : (location || '');
            // Justification is per-item in the row immediately following (justification-row)
            let justificationInput = null;
            if (type === 'correction' || type === 'loss') {
                const justificationRow = row.nextElementSibling;
                if (justificationRow && justificationRow.classList.contains('justification-row')) {
                    justificationInput = justificationRow.querySelector('.input-justification');
                }
            }
            const itemJustification = justificationInput ? justificationInput.value?.trim() : null;

            // Validate justification per item for correction/loss
            if ((type === 'correction' || type === 'loss') && !itemJustification) {
                openConfirmationModal(
                    'Validation Error',
                    'Justification is required for each drug item in correction and loss transactions.',
                    () => { },
                    false,
                    'OK',
                );
                return;
            }

            if (!drugId || isNaN(quantity)) {
                openConfirmationModal(
                    'Validation Error',
                    'Please fill in all drug and quantity fields.',
                    () => { },
                    false,
                    'OK',
                    'OK'
                );
                return;
            }

            // Validation: Check for negative numbers (only correction allows negatives)
            if (type !== 'correction' && quantity < 0) {
                openConfirmationModal(
                    'Validation Error',
                    `${type === 'restock' ? 'Restock' : type === 'use' ? 'Use' : type === 'relocate' ? 'Relocate' : 'Loss'} transactions cannot have negative quantities.`,
                    () => { },
                    false,
                    'OK',
                );
                return;
            }

            // For use transactions, quantity should be negative (already validated as whole number above)
            if (type === 'use') {
                quantity = -Math.abs(quantity);
            }

            // For loss transactions, quantity should be negative
            if (type === 'loss') {
                quantity = -Math.abs(quantity);
            }

            // Bulk use validation: whole numbers only (already validated per item above)
            if (type === 'use' && isBulk) {
                const roundedQty = Math.round(Math.abs(quantity));
                if (Math.abs(Math.abs(quantity) - roundedQty) >= 0.0001) {
                    openConfirmationModal(
                        'Validation Error',
                        'Bulk use requires whole numbers only (no decimals).',
                        () => { },
                        false,
                        'OK',
                    );
                    return;
                }
                quantity = -roundedQty;
            }

            // Round to 4 decimal places
            quantity = roundToPrecision(quantity);

            items.push({
                drugId,
                location: type === 'relocate' ? null : location,
                quantity,
                justification: itemJustification || null,
                administered: administered !== null ? administered : undefined,
                discarded: discarded !== null ? discarded : undefined
            });
        }

        // Additional validations
        if (type === 'use' && !isBulk && !patient) {
            openConfirmationModal(
                'Validation Error',
                'Patient name is required for single use transactions.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        // Location validation (for use, restock, correction, loss)
        if ((type === 'use' || type === 'restock' || type === 'correction' || type === 'loss') && !location) {
            openConfirmationModal(
                'Validation Error',
                'Location is required.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        // Bulk code is optional - will be auto-generated server-side if not provided

        if ((type === 'use' || type === 'relocate') && !provider) {
            openConfirmationModal(
                'Validation Error',
                'Provider is required for use and relocate transactions.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        if (type === 'relocate' && (!fromLocation || !toLocation || fromLocation === toLocation)) {
            openConfirmationModal(
                'Validation Error',
                'From and To locations must be different.',
                () => { },
                false,
                'OK',
            );
            return;
        }

        // Prepare payload
        const payload = {
            type,
            transactionDate,
            items,
            patient: type === 'use' && !isBulk ? patient : null,
            batchCode: type === 'use' && isBulk ? (bulkCode || null) : null, // Use provided bulk code or null (will be auto-generated)
            isBulk: type === 'use' ? isBulk : undefined, // Send isBulk flag to backend (true/false/undefined)
            provider: (type === 'use' || type === 'relocate') ? provider : null,
            supplier: type === 'restock' ? supplier : null,
            orderNumber: type === 'restock' ? orderNumber : null,
            fromLocation: type === 'relocate' ? fromLocation : null,
            toLocation: type === 'relocate' ? toLocation : null,
        };

        // Submit
        // Check for Negative Stock (Soft Block)
        const warnings = [];
        items.forEach(item => {
            const drug = appData.drugInventory?.find(d => d.id === item.drugId);
            if (!drug) {return;}

            let currentStock;
            let projectedStock;
            const drugName = drug.shortName || drug.name || 'Unknown Drug';

            if (type === 'relocate') {
                // Relocate check: Check SOURCE location
                const locId = fromLocation;
                const locName = getLocationName(locId);
                currentStock = drug.stockLevels?.[locId] || 0;
                // item.quantity is positive for relocate input, effectively removed from source
                projectedStock = currentStock - item.quantity;

                if (projectedStock < 0) {
                    warnings.push(`<li><strong>${sanitizeHTML(drugName)}</strong> at <strong>${sanitizeHTML(locName)}</strong>: Current ${roundToPrecision(currentStock)}, Result ${roundToPrecision(projectedStock)}</li>`);
                }
            } else {
                // Use, Loss, Correction, Restock check: Check TARGET location
                const locId = location;
                const locName = getLocationName(locId);
                currentStock = drug.stockLevels?.[locId] || 0;
                // item.quantity is already signed correctly for use/loss/restock (-ve for use/loss, +ve for restock)
                projectedStock = currentStock + item.quantity;

                if (projectedStock < 0) {
                    warnings.push(`<li><strong>${sanitizeHTML(drugName)}</strong> at <strong>${sanitizeHTML(locName)}</strong>: Current ${roundToPrecision(currentStock)}, Result ${roundToPrecision(projectedStock)}</li>`);
                }
            }
        });

        const executeSave = async () => {
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Processing...';
                submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
            }
            const result = await saveDrugTransaction(payload);
            if (result && result.success) {
                closeForm();
                renderSubmissionFeedback(result, payload.type);
            } else {
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalBtnText;
                    submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                }
            }
        };

        if (warnings.length > 0) {
            const message = `
                <p class="mb-2 text-gray-700 dark:text-gray-300">This transaction will result in negative stock for the following items:</p>
                <ul class="list-disc pl-5 mb-4 text-sm text-red-600 dark:text-red-400 space-y-1">
                    ${warnings.join('')}
                </ul>
                <p class="text-gray-600 dark:text-gray-400">Do you want to proceed anyway?</p>
            `;
            openConfirmationModal(
                'Negative Stock Warning',
                message,
                executeSave,
                true, // Danger style
                'Proceed'
            );
        } else {
            // No warnings, proceed immediately
            await executeSave();
        }
    } catch (error) {
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalBtnText;
            submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
        }
        console.error('Error submitting drug transaction:', error);
        openConfirmationModal(
            'Error',
            error.message || 'Failed to save transaction. Please try again.',
            () => { },
            false,
            'OK',
        );
    }
}

// --- HELPER FUNCTIONS ---

/**
 * Formats transaction drugs for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for drug column.
 */
function formatTransactionDrugs(tx) {
    // Audit transactions have empty payload but should still display
    if (tx.type === 'audit') {
        return '<span class="text-gray-500 italic">Audit</span>';
    }
    if (!tx.payload || !Array.isArray(tx.payload) || tx.payload.length === 0) {return '-';}

    // Relocate grouping (or Void of Relocate)
    const isVoid = tx.type === 'void';
    const isRelocateOrVoid = tx.type === 'relocate' || isVoid;

    if (isRelocateOrVoid && tx.payload.length >= 2) {
        // ... (check pattern matching)
        const drugGroups = {};
        let isRelocatePattern = false;

        tx.payload.forEach((item) => {
            if (!drugGroups[item.drugId]) {
                drugGroups[item.drugId] = { count: 0 };
            }
            drugGroups[item.drugId].count++;
        });

        // For relocate/void-relocate, we expect pairs (2 items per drug usually)
        if (Object.values(drugGroups).some(g => g.count >= 2)) {
            isRelocatePattern = true;
        }

        if (isRelocatePattern) {
            let html = '';
            Object.keys(drugGroups).forEach((drugId) => {
                const drug = appData.drugInventory?.find((d) => d.id === drugId);
                const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown Drug') : 'Unknown Drug';
                html += `
                    <div class="mb-1 last:mb-0">
                        <span class="font-medium text-gray-900">${sanitizeHTML(drugDisplayName)}</span>
                    </div>
                `;
            });
            return html || '-';
        }
    }

    // Standard display (one line per payload item)
    let html = '';
    tx.payload.forEach((item) => {
        if (item.drugId) {
            const drug = appData.drugInventory?.find((d) => d.id === item.drugId);
            const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown') : 'Unknown';
            html += `
                <div class="mb-1 last:mb-0">
                    <span class="font-medium text-gray-900">${sanitizeHTML(drugDisplayName)}</span>
                </div>
            `;
        }
    });

    return html || '-';
}

/**
 * Formats transaction locations for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for location column.
 */
function formatTransactionLocations(tx) {
    // Audit transactions have empty payload but should still display
    if (tx.type === 'audit') {
        return '<span class="text-gray-500 italic">-</span>';
    }
    if (!tx.payload || !Array.isArray(tx.payload) || tx.payload.length === 0) {return '-';}

    // Relocate grouping (or Void of Relocate)
    const isVoid = tx.type === 'void';
    const isRelocateOrVoid = tx.type === 'relocate' || isVoid;

    if (isRelocateOrVoid && tx.payload.length >= 2) {
        const drugGroups = {};
        let isRelocatePattern = false;

        tx.payload.forEach((item) => {
            if (!drugGroups[item.drugId]) {
                drugGroups[item.drugId] = { source: null, dest: null };
            }
            if (item.quantity < 0) {
                drugGroups[item.drugId].source = item;
            } else {
                drugGroups[item.drugId].dest = item;
            }
        });

        if (Object.values(drugGroups).some(g => g.source && g.dest)) {
            isRelocatePattern = true;
        }

        if (isRelocatePattern) {
            let html = '';
            Object.keys(drugGroups).forEach((drugId) => {
                const group = drugGroups[drugId];
                const sourceName = getLocationName(group.source?.location);
                const destName = getLocationName(group.dest?.location);

                if (group.source && group.dest) {
                    html += `
                        <div class="mb-1 last:mb-0 text-xs">
                            ${sourceName} <span class="material-symbols-outlined text-[10px] align-middle px-1">arrow_forward</span> ${destName}
                        </div>
                    `;
                }
            });
            return html || '-';
        }
    }

    // For standard transactions AND manual corrections (single-location restriction),
    // deduplicate locations to reduce visual clutter.
    // Only Audit Corrections (which can span locations) need strict line-by-line mapping.
    if (tx.type !== 'correction' || !tx.auditId) {
        const uniqueLocations = new Set();
        let html = '';
        tx.payload.forEach((item) => {
            if (item.location && !uniqueLocations.has(item.location)) {
                uniqueLocations.add(item.location);
                const locName = getLocationName(item.location);
                html += `
                    <div class="mb-1 last:mb-0">
                        <span class="text-gray-500">${sanitizeHTML(locName)}</span>
                    </div>
                `;
            }
        });
        return html || '-';
    }

    // Standard display for Corrections (one line per payload item)
    // Needed to ensure alignment with multi-drug correction rows
    let html = '';
    tx.payload.forEach((item) => {
        if (item.location) {
            const locName = getLocationName(item.location);
            html += `
                <div class="mb-1 last:mb-0">
                    <span class="text-gray-500">${sanitizeHTML(locName)}</span>
                </div>
            `;
        }
    });

    return html || '-';
}

/**
 * Formats transaction details (drug names and locations) for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for details column.
 * @deprecated Use formatTransactionDrugs and formatTransactionLocations instead.
 */
function _formatTransactionDetails(tx) {
    if (!tx.payload || !Array.isArray(tx.payload) || tx.payload.length === 0) {return '-';}

    // Relocate grouping
    if (tx.type === 'relocate' && tx.payload.length >= 2) {
        // Group by drugId
        const drugGroups = {};
        tx.payload.forEach((item) => {
            if (!drugGroups[item.drugId]) {
                drugGroups[item.drugId] = { source: null, dest: null };
            }
            if (item.quantity < 0) {
                drugGroups[item.drugId].source = item;
            } else {
                drugGroups[item.drugId].dest = item;
            }
        });

        let html = '';
        Object.keys(drugGroups).forEach((drugId) => {
            const group = drugGroups[drugId];
            const drug = appData.drugInventory?.find((d) => d.id === drugId);
            const sourceName = getLocationName(group.source?.location);
            const destName = getLocationName(group.dest?.location);

            if (group.source && group.dest) {
                const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown Drug') : 'Unknown Drug';
                html += `
                    <div class="mb-1 last:mb-0">
                        <span class="font-medium text-gray-900">${sanitizeHTML(drugDisplayName)}</span>
                        <div class="text-xs text-gray-500">
                            ${sourceName} <span class="material-symbols-outlined text-[10px] align-middle px-1">arrow_forward</span> ${destName}
                        </div>
                    </div>
                `;
            }
        });
        return html || '-';
    }

    // Standard display (just drug names and locations)
    let html = '';
    tx.payload.forEach((item) => {
        const drug = appData.drugInventory?.find((d) => d.id === item.drugId);
        const drugDisplayName = drug ? (drug.shortName || drug.name || 'Unknown') : 'Unknown';
        const locName = getLocationName(item.location);

        html += `
            <div class="mb-1 last:mb-0">
                <span class="font-medium text-gray-900">${sanitizeHTML(drugDisplayName)}</span>
                <span class="text-gray-500 text-xs ml-1">(${locName})</span>
            </div>
        `;
    });

    return html;
}

/**
 * Formats transaction quantities with colors and signs for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for quantity column.
 */
function formatTransactionQuantities(tx) {
    // Audit transactions have empty payload - show as audit
    if (tx.type === 'audit') {
        return '<span class="text-gray-500 italic">Audit</span>';
    }
    if (!tx.payload || !Array.isArray(tx.payload) || tx.payload.length === 0) {return '-';}

    // Relocate grouping (or Void of a relocate)
    const isVoid = tx.type === 'void';
    const isRelocateOrVoid = tx.type === 'relocate' || isVoid;

    // Check if it's a relocate-like pattern (at least 2 items)
    if (isRelocateOrVoid && tx.payload.length >= 2) {
        const drugGroups = {};
        let isRelocatePattern = false;

        tx.payload.forEach((item) => {
            if (!drugGroups[item.drugId]) {
                drugGroups[item.drugId] = { source: null, dest: null };
            }
            if (item.quantity < 0) {
                drugGroups[item.drugId].source = item;
            } else {
                drugGroups[item.drugId].dest = item;
            }
        });

        // Verify we actually have pairs
        if (Object.values(drugGroups).some(g => g.source && g.dest)) {
            isRelocatePattern = true;
        }

        // Only render as relocate if it matches the pattern
        if (isRelocatePattern) {
            let html = '';
            Object.keys(drugGroups).forEach((drugId) => {
                const group = drugGroups[drugId];
                const qty = Math.abs(group.source?.quantity || 0);

                if (group.source && group.dest) {
                    const colorAttr = isVoid ? 'class="text-sm font-bold text-gray-900"' : 'class="text-sm font-bold" style="color: #0080FE;"';
                    html += `
                        <div class="mb-1 last:mb-0">
                            <div ${colorAttr}>${roundToPrecision(qty)} amps</div>
                        </div>
                    `;
                }
            });
            return html || '-';
        }
    }

    // Standard display
    let html = '';
    tx.payload.forEach((item) => {
        // Determine color and sign based on transaction type and quantity
        let colorClass = 'text-gray-600';
        let sign = '';

        if (tx.type === 'restock') {
            // Restock: Always green with +
            colorClass = 'text-green-600';
            sign = '+';
        } else if (tx.type === 'use' || tx.type === 'loss') {
            // Use/Loss: Always red with -
            colorClass = 'text-red-600';
            sign = '-';
        } else if (tx.type === 'correction') {
            // Correction: Green if positive, Red if negative
            if (item.quantity > 0) {
                colorClass = 'text-green-600';
                sign = '+';
            } else {
                colorClass = 'text-red-600';
                sign = '-';
            }
        } else if (tx.type === 'relocate') {
            // Relocate: Blue
            colorClass = '';
        } else if (tx.type === 'void') {
            // Void: Black text, show signs
            colorClass = 'text-gray-900'; // "Black"
            if (item.quantity > 0) {
                sign = '+';
            } else if (item.quantity < 0) {
                sign = '-';
            }
        }

        // Breakdown text for administered/discarded
        let breakdownHtml = '';
        if (tx.type === 'use' && (item.administered !== undefined || item.discarded !== undefined)) {
            const admin = item.administered !== undefined ? roundToPrecision(item.administered) : 0;
            const disc = item.discarded !== undefined ? roundToPrecision(item.discarded) : 0;
            breakdownHtml = `<span class="text-xs text-gray-400 font-normal ml-1">(${admin}/${disc})</span>`;
        }

        html += `
            <div class="mb-1 last:mb-0">
                <div class="text-sm font-bold ${colorClass || ''}" ${colorClass === '' ? 'style="color: #0080FE;"' : ''}>
                    ${sign}${roundToPrecision(Math.abs(item.quantity))} amps
                    ${breakdownHtml}
                </div>
            </div>
        `;
    });

    return html;
}

/**
 * Formats transaction balances for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for balance column.
 */
function formatTransactionBalances(tx) {
    if (tx.type === 'audit') {
        return '<span class="text-gray-500 italic">-</span>';
    }

    // Relocate transactions are complex (show - for now to avoid confusion as they involve 2 balances)
    if (tx.type === 'relocate') {
        return '<span class="text-gray-300">-</span>';
    }

    // NEW: Check if this is a VOID of a relocate transaction
    if (tx.type === 'void' && tx.voidedTransactionId) {
        // Try to find original transaction to check its type
        const originalTx = (appData.drugTransactions || []).find(t => t.id === tx.voidedTransactionId);
        if (originalTx && originalTx.type === 'relocate') {
            return '<span class="text-gray-300">-</span>';
        }
    }

    if (!tx.payload || !Array.isArray(tx.payload) || tx.payload.length === 0) {
        return '<span class="text-gray-300">-</span>';
    }

    let html = '';
    // We iterate payload to match vertical rhythm of drugs/qty columns
    tx.payload.forEach(item => {
        let balText = '<span class="text-gray-300">-</span>';

        // Prefer item-level balance
        if (item.balanceAfter !== undefined && item.balanceAfter !== null) {
            balText = `<span class="text-gray-500">${roundToPrecision(item.balanceAfter)}</span>`;
        }
        html += `
            <div class="mb-1 last:mb-0">
                ${balText}
            </div>
        `;
    });

    return html || '<span class="text-gray-300">-</span>';
}

/**
 * Formats transaction notes (patient name, batch code, justification) for display in history table.
 * @param {object} tx - Transaction object.
 * @returns {string} HTML string for notes column.
 */
function formatTransactionNotes(tx) {
    let html = '';

    // Audit transaction - show audit details
    if (tx.type === 'audit' && tx.auditId) {
        const audit = appData.drugAudits?.find(a => a.id === tx.auditId);
        if (audit) {
            const startedBy = appData.users?.find(u => u.id === audit.startedBy);
            const finalizedBy = appData.users?.find(u => u.id === audit.finalizedBy);
            const startedByName = startedBy ? sanitizeHTML(startedBy.name) : 'Unknown';
            const finalizedByName = finalizedBy ? sanitizeHTML(finalizedBy.name) : 'Unknown';

            html += `<div class="text-sm text-gray-500 mb-1">Audit</div>`;
            html += `<div class="text-xs text-gray-400 mt-1">Started by: ${startedByName}</div>`;
            if (audit.status === 'finalized') {
                html += `<div class="text-xs text-gray-400">Finalised by: ${finalizedByName}</div>`;
            }
            return html;
        }
    }

    // Void Transaction - Specific formatting
    if (tx.type === 'void' && tx.voidedTransactionId) {
        // Find original transaction to get details
        const originalTx = (appData.drugTransactions || []).find(t => (t.id === tx.voidedTransactionId || t._originalId === tx.voidedTransactionId));

        // Build reference text
        if (originalTx) {
            const dateStr = formatDate(originalTx.transactionDate);
            html += `<div class="text-xs text-gray-500 mb-1">Voiding: ${dateStr} - ${sanitizeHTML(originalTx.type)}</div>`;
        } else {
            // No ID shown as requested
            html += `<div class="text-xs text-gray-500 mb-1">Voiding: Unknown Transaction</div>`;
        }

        // Show the void reason (from justification field)
        const reasons = [];

        // Clean up justification (remove auto-generated prefix if present to just show user reason)
        if (tx.justification) {
            let userReason = tx.justification;
            // Backend format: "Voiding Transaction: ID. Reason: ACTUAL_REASON"
            const parts = userReason.split('Reason: ');
            if (parts.length > 1) {
                userReason = parts[1];
            }
            reasons.push(sanitizeHTML(userReason));
        }

        // Use legacy field if justification missing
        if (reasons.length === 0 && tx.voidReason) {
            reasons.push(sanitizeHTML(tx.voidReason));
        }

        // Also show justification from payload if present (and diff from main justification)
        if (tx.payload && Array.isArray(tx.payload)) {
            tx.payload.forEach(item => {
                const itemJust = item.justification ? sanitizeHTML(item.justification) : '';
                if (itemJust && itemJust.trim() && !reasons.includes(itemJust)) {
                    reasons.push(itemJust);
                }
            });
        }

        if (reasons.length > 0) {
            // Enhanced extraction to handle both raw reason and "Reason: ..." format
            let displayReason = reasons[0];
            if (displayReason.includes('Reason: ')) {
                displayReason = displayReason.split('Reason: ')[1];
            }
            html += `<div class="text-xs text-gray-500 italic mt-1">${displayReason}</div>`;
        }
        return html;
    }

    // Voided Transaction (Original) - Add strikethrough visual
    if (tx.isVoided) {
        html += `<div class="text-xs text-red-600 font-bold mb-1">VOIDED</div>`;
        // Prefer voidReason but fall back to extracting from justification if needed
        let displayReason = tx.voidReason || (tx.justification?.includes('Reason: ') ? tx.justification.split('Reason: ')[1] : null);

        if (displayReason) {
            // Further sanitize and extract if still in "Reason: " format
            if (displayReason.includes('Reason: ')) {
                displayReason = displayReason.split('Reason: ')[1];
            }
            html += `<div class="text-xs text-gray-400 italic">Reason: ${sanitizeHTML(displayReason)}</div>`;
        }
    }

    // Provider Name (for Use/Relocate)
    if ((tx.type === 'use' || tx.type === 'relocate') && tx.provider) {
        // provider is stored as ID, need to resolve name
        const providerUser = appData.users?.find(u => u.id === tx.provider);
        const providerName = providerUser ? providerUser.name : 'Unknown';
        html += `<div class="text-xs text-gray-500 mb-1">Provider: ${sanitizeHTML(providerName)}</div>`;
    }

    // Patient name or batch code (mid grey)
    if (tx.patientName) {
        html += `<div class="text-xs text-gray-500 mb-1">Patient: ${sanitizeHTML(tx.patientName)}</div>`;
    } else if (tx.batchCode) {
        html += `<div class="text-xs text-gray-500 mb-1">${sanitizeHTML(tx.batchCode)}</div>`;
    }

    // Supplier (Restock)
    if (tx.supplier) {
        const supId = tx.supplier;
        const sup = appData.config?.drugs?.suppliers?.find(s => s.id === supId);

        if (sup) {
            html += `<div class="text-xs text-gray-500 mb-1">Supplier: ${sanitizeHTML(sup.name)}</div>`;
        } else {
            html += `<div class="text-xs text-gray-500 mb-1">Supplier: ${sanitizeHTML(supId)}</div>`;
        }
    }

    // Order Number (Restock/Stocktake)
    if (tx.orderNumber) {
        html += `<div class="text-xs text-gray-500 mb-1">ON: ${sanitizeHTML(tx.orderNumber)}</div>`;
    }

    // Correction from audit - show audit reference
    if (tx.type === 'correction' && tx.auditId) {
        html += `<div class="text-xs text-gray-400 italic mt-1">From audit</div>`;
    }

    // Per-item justification (for correction/loss)
    if (tx.type === 'correction' || tx.type === 'loss') {
        const justifications = [];
        if (tx.payload && Array.isArray(tx.payload)) {
            tx.payload.forEach(item => {
                if (item.justification && item.justification.trim()) {
                    const drug = appData.drugInventory?.find(d => d.id === item.drugId);
                    const drugName = drug ? (drug.shortName || drug.name || 'Unknown') : 'Unknown';
                    justifications.push(`${sanitizeHTML(drugName)}: ${sanitizeHTML(item.justification)}`);
                }
            });
        }
        // Fallback to transaction-level justification if no per-item justifications
        if (justifications.length === 0 && tx.justification) {
            justifications.push(sanitizeHTML(tx.justification));
        }

        if (justifications.length > 0) {
            justifications.forEach(just => {
                html += `<div class="text-xs text-gray-500 italic mt-1">${just}</div>`;
            });
        }
    }

    // Approver Note (General)
    if (tx.approverNote) {
        html += `<div class="text-xs text-gray-500 italic mt-1 border-l-2 border-gray-300 pl-1">Approver: ${sanitizeHTML(tx.approverNote)}</div>`;
    }

    return html || '-';
}

/**
 * Checks if a transaction can be voided (not on or after any finalised audit start date).
 * @param {object} tx - Transaction object.
 * @returns {boolean} True if transaction can be voided.
 */
function canVoidTransaction(tx) {
    if (!tx || !tx.transactionDate) {return true;}

    // RULE: Corrections created as part of an audit cannot be voided manually.
    // They are permanent records of the audit outcome.
    if (tx.type === 'correction' && tx.auditId) {
        return false;
    }

    // Get all finalised audits
    const finalizedAudits = appData.drugAudits?.filter(a => a.status === 'finalized') || [];
    if (finalizedAudits.length === 0) {return true;}

    // Parse transaction date (keep full timestamp)
    let txDate;
    try {
        if (tx.createdAt?.toDate && typeof tx.createdAt.toDate === 'function') {
            // Prefer createdAt for precise chronological ordering if available
            txDate = tx.createdAt.toDate();
        } else if (tx.transactionDate?.toDate && typeof tx.transactionDate.toDate === 'function') {
            txDate = tx.transactionDate.toDate();
        } else if (typeof tx.transactionDate === 'string') {
            txDate = new Date(tx.transactionDate);
        } else if (tx.transactionDate?.seconds) {
            txDate = new Date(tx.transactionDate.seconds * 1000);
        } else {
            txDate = new Date(tx.transactionDate);
        }
    } catch (e) {
        console.warn('Error parsing transaction date:', e);
        return true;
    }

    // Check if transaction is earlier than any finalised audit start time
    for (const audit of finalizedAudits) {
        if (!audit.startedAt) {continue;}

        let auditStartDate;
        try {
            if (audit.startedAt?.toDate && typeof audit.startedAt.toDate === 'function') {
                auditStartDate = audit.startedAt.toDate();
            } else if (typeof audit.startedAt === 'string') {
                auditStartDate = new Date(audit.startedAt);
            } else if (audit.startedAt?.seconds) {
                auditStartDate = new Date(audit.startedAt.seconds * 1000);
            } else {
                auditStartDate = new Date(audit.startedAt);
            }
        } catch (e) {
            console.warn('Error parsing audit start date:', e);
            continue;
        }


        // If transaction is OLDER than audit start (part of this audit or prior), cannot void
        // Compare timestamps
        if (txDate.getTime() < auditStartDate.getTime()) {
            return false;
        }
    }

    return true;
}

/**
 * Formats transaction status badge with approver info if available.
 * @param {object} tx - Transaction object (needed for approver info).
 * @returns {string} HTML string for status badge.
 */
function formatTransactionStatus(tx) {
    const status = typeof tx === 'string' ? tx : tx.status;
    let statusClass = 'bg-gray-100 text-gray-800';
    if (status === 'approved') {statusClass = 'bg-green-100 text-green-800';}
    if (status === 'pending_approval') {statusClass = 'bg-yellow-100 text-yellow-800';}
    if (status === 'pending_finalization') {statusClass = 'bg-blue-100 text-blue-800';}
    if (status === 'rejected') {statusClass = 'bg-red-100 text-red-800';}
    if (status === 'void') {statusClass = 'bg-gray-200 text-gray-500';}

    let statusText = sanitizeHTML(status.replace('_', ' '));
    let actorName = '';

    // Get actor name based on status
    if (typeof tx === 'object') {
        let actorId = null;
        if (status === 'approved' && tx.approvedBy) {
            actorId = tx.approvedBy;
        } else if (status === 'void' && tx.voidedBy) {
            actorId = tx.voidedBy;
        } else if (status === 'rejected' && tx.rejectedBy) {
            actorId = tx.rejectedBy;
        }

        if (actorId) {
            const actor = appData.users?.find(u => u.id === actorId);
            if (actor) {
                actorName = sanitizeHTML(actor.name);
            }
        }
    }

    // Format: status and actor name on same line with better spacing
    if (actorName) {
        statusText = `${statusText} <span class="text-[10px] opacity-70 ml-1.5 whitespace-nowrap normal-case">${actorName}</span>`;
    }

    return `
        <span class="px-2 py-0.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${statusClass} whitespace-nowrap capitalize">
            ${statusText}
        </span>
    `;
}

/**
 * Gets location display name from ID.
 * @param {string} id - Location ID.
 * @returns {string} Location name or ID if not found.
 */
function getLocationName(id) {
    if (!id) {return 'Unknown';}
    const loc = appData.config?.drugs?.locations?.find((l) => l.id === id);
    return loc ? sanitizeHTML(loc.name) : sanitizeHTML(id);
}

/**
 * Handles print audit action.
 * Delegates to the Drugs audit report owner.
 * @param {string} auditId - Audit ID.
 */
function handlePrintAudit(auditId) {
    // Try to find audit in drugAudits collection first
    let audit = appData.drugAudits?.find(a => a.id === auditId);

    // If not found, try to construct from transaction data
    if (!audit) {
        const tx = appData.drugTransactions?.find(t => t.auditId === auditId && t.type === 'audit');
        if (tx) {
            // Merge transaction data with minimal audit structure
            audit = {
                id: auditId,
                auditNumber: tx.auditNumber,
                resultsStatus: tx.resultsStatus,
                startedBy: tx.performedBy,
                finalizedBy: tx.approvedBy,
                startedAt: tx.startedAt,
                finalizedAt: tx.finalizedAt,
                // Note: snapshot, actuals, discrepancyNotes won't be available from transaction
                // This is a fallback - ideally audit should be in drugAudits
            };
        }
    }

    if (!audit) {
        openConfirmationModal(
            'Error',
            'Audit data not found. Please refresh the page and try again.',
            () => { },
            false,
            'OK',
        );
        return;
    }

    // If audit doesn't have snapshot/actuals, try to get from drugAudits again
    if (!audit.snapshot || !audit.actuals) {
        const fullAudit = appData.drugAudits?.find(a => a.id === auditId);
        if (fullAudit) {
            audit = fullAudit;
        } else {
            openConfirmationModal(
                'Error',
                'Full audit details not available. The audit may still be in progress.',
                () => { },
                false,
                'OK',
            );
            return;
        }
    }

    // Call shared printing function
    printAuditReport(audit);
}

/**
 * Handles void transaction action.
 * @param {string} txId - Transaction ID.
 */
function handleVoidTransaction(txId) {
    // Use a custom modal that accepts input instead of the browser prompt
    const modalContent = `
        <div class="space-y-4 px-1">
            <p class="text-gray-600 dark:text-gray-300">Are you sure you want to void this transaction? This will reverse the stock impact.</p>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Voiding</label>
                <textarea id="void-reason-input" rows="1" class="${DRUG_MODAL_AUTO_TEXTAREA_CLASSES}" data-auto-grow="true" placeholder="Add a reason"></textarea>
            </div>
        </div>
    `;

    openConfirmationModal(
        'Void Transaction',
        modalContent,
        () => {
            const reasonInput = document.getElementById('void-reason-input');
            const reason = reasonInput ? reasonInput.value.trim() : '';

            if (!reason) {
                return requireDrugActionFieldValue(
                    document.getElementById('confirm-modal'),
                    reasonInput,
                    'Reason is required to void this transaction.',
                );
            }

            voidTransaction(txId, reason).then((result) => {
                renderSubmissionFeedback(result, 'void');

                // Refresh the drugs view if currently viewing history to show voided status immediately (optimistic)
                if (state.currentView === 'drugs' && state.drugsView === 'history') {
                    renderDrugsView();
                }
            }).catch((err) => {
                console.error('Failed to void transaction:', err);
                let errorMessage = 'Failed to void transaction. Please try again.';
                if (err.message) {errorMessage = err.message;}

                openConfirmationModal(
                    'Error',
                    errorMessage,
                    () => { },
                    false,
                    'OK',
                );
            });
        },
        true, // Danger (red button)
        'Void Transaction',
        null,
        '',
        'Cancel',
        { keepOpenOnFalse: true },
    );
    const confirmModal = document.getElementById('confirm-modal');
    applyDrugControlSurface(confirmModal, appData);
    bindDrugNativeFieldSurface(confirmModal);
}

// --- HELPER: Check if Restock is Allowed ---
function isRestockAllowed() {
    const audits = appData.drugAudits || [];
    // 1. Find last finalized audit
    const lastAudit = audits
        .filter(a => a.status === 'finalized')
        .sort((a, b) => new Date(b.finalizedAt) - new Date(a.finalizedAt))[0];

    // 2. Get Config Window (Default 24h)
    const windowHours = appData.config?.drugs?.auditRequirements?.restockWindowHours || 24;

    // 3. Check Validity
    if (!lastAudit) {return { allowed: false, reason: `Audit required within ${windowHours} hours.` };}

    // Safety check for finalizedAt (handle timestamp vs string)
    let finalizedTime;
    if (lastAudit.finalizedAt?.toDate) {finalizedTime = lastAudit.finalizedAt.toDate().getTime();}
    else {finalizedTime = new Date(lastAudit.finalizedAt).getTime();}

    const now = Date.now();
    const diffHours = (now - finalizedTime) / (1000 * 60 * 60);

    if (diffHours > windowHours) {
        return {
            allowed: false,
            reason: `Audit required within ${windowHours} hours.`
        };
    }

    return { allowed: true };
}

/**
 * Renders the Action Centre for users with action permissions.
 * Displays action tiles with grey icons, yellow hover, red active states.
 * @param {HTMLElement} container - Container element to render into.
 */
export function renderActionCentre(container) {
    if (!container) {return;}

    const profile = state.currentUserProfile;
    const perms = profile?.drugPermissions || {};
    const isAdmin = profile?.role === 'admin';
    const isLocked = !!appData.activeAudit;
    const isMaintenanceLocked = isDrugsUiLocked();
    const isActionLocked = isLocked || isMaintenanceLocked;
    const lockMessage = isLocked
        ? 'Audit in Progress - Transactions are locked'
        : 'Drugs system locked for maintenance';

    // Restock Guardrail Check
    const restockCheck = isRestockAllowed();

    // Define all potential actions
    const actions = [
        { id: 'use', label: 'Log Use', icon: 'vaccines', perm: 'logUse' },
        {
            id: 'restock',
            label: 'Restock',
            icon: 'inventory_2',
            perm: 'restock',
            disabled: !restockCheck.allowed,
            disabledReason: restockCheck.reason
        },
        { id: 'relocate', label: 'Relocate', icon: 'swap_horiz', perm: 'relocate' },
        { id: 'correction', label: 'Correction', icon: 'edit', perm: 'correction' },
        { id: 'loss', label: 'Record Loss', icon: 'warning', perm: 'logLoss' }
    ];

    // Filter allowed actions
    const allowedActions = actions.filter(a => {
        return isAdmin || perms.drugAdmin === true || perms[a.perm] === true;
    });

    // Check for Start Audit permission
    const canStartAudit = isAdmin || perms.drugAdmin === true || perms.startAudit === true;
    const _canViewAudit = isAdmin || perms.drugAdmin === true || perms.viewAudits === true;

    if (allowedActions.length === 0 && !canStartAudit) {
        container.innerHTML = `
            <div class="text-center py-12">
                <span class="material-symbols-outlined text-gray-300 text-6xl mb-4">block</span>
                <p class="text-gray-500 text-lg">You do not have permission to perform any actions.</p>
            </div>
        `;
        return;
    }

    container.innerHTML = `
        <div class="mx-auto max-w-3xl px-1 py-6 sm:px-4 sm:py-12">
             ${isActionLocked ? `
                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-lg flex items-center justify-center">
                    <span class="material-symbols-outlined text-yellow-600 mr-2">lock</span>
                    <span class="text-yellow-800 font-medium">${lockMessage}</span>
                </div>
            ` : ''}

            <h2 class="mb-6 text-center text-2xl font-bold text-gray-800 sm:mb-10 sm:text-3xl">Action Centre</h2>

            <div class="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
                ${allowedActions.map(action => {
        const isDisabled = isActionLocked || action.disabled;
        const tooltip = isLocked
            ? 'Audit in Progress'
            : (isMaintenanceLocked ? 'Drugs system locked for maintenance' : (action.disabledReason || ''));

        return `
                    <button class="action-tile group relative flex aspect-[1/0.82] flex-col items-center justify-center rounded-lg bg-white p-3 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dfc-brand-primary-ring)] sm:aspect-square sm:p-4
                        ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}"
                        data-action="${action.id}"
                        ${isDisabled ? 'disabled' : ''}
                        title="${tooltip}">

                        <div class="action-icon-wrapper mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 transition-all duration-300 sm:mb-3 sm:h-20 sm:w-20">
                            <span class="material-symbols-outlined text-3xl text-black transition-colors duration-300 sm:text-4xl">${action.icon}</span>
                        </div>
                        <span class="action-label text-center text-sm font-semibold leading-tight text-black transition-colors duration-300 sm:text-base">${action.label}</span>

                        ${action.disabledReason ? `<div class="mt-2 text-[10px] text-gray-400 font-medium text-center w-full">${sanitizeHTML(action.disabledReason)}</div>` : ''}
                    </button>
                `}).join('')}
                ${canStartAudit ? `
                    <button id="btn-start-audit-action" class="action-tile group flex aspect-[1/0.82] flex-col items-center justify-center rounded-lg bg-white p-3 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dfc-brand-primary-ring)] sm:aspect-square sm:p-4 ${isActionLocked ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}" ${isActionLocked ? 'disabled' : ''} title="${isLocked ? 'Audit in Progress' : (isMaintenanceLocked ? 'Drugs system locked for maintenance' : '')}">
                        <div class="action-icon-wrapper mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 transition-all duration-300 sm:mb-3 sm:h-20 sm:w-20">
                            <span class="material-symbols-outlined text-3xl text-black transition-colors duration-300 sm:text-4xl">fact_check</span>
                        </div>
                        <span class="action-label text-center text-sm font-semibold leading-tight text-black transition-colors duration-300 sm:text-base">Start Audit</span>
                    </button>
                ` : ''}
            </div>
        </div>

        <style>
            .action-tile {
                transition: box-shadow 0.2s, transform 0.1s;
                border: 1px solid #f3f4f6;
                aspect-ratio: 1 / 0.82;
            }
            @media (min-width: 640px) {
                .action-tile {
                    aspect-ratio: 1 / 1;
                }
            }
            .action-tile:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
            }
            /* Only apply hover colors if NOT disabled */
            .action-tile:where(:not(:disabled):not(.disabled)):hover .action-icon-wrapper {
                background-color: var(--dfc-brand-primary) !important;
            }
            .action-tile:where(:not(:disabled):not(.disabled)):hover span.material-symbols-outlined {
                color: var(--dfc-brand-primary-contrast) !important;
            }
            .action-tile:where(:not(:disabled):not(.disabled)):hover .action-label {
                color: var(--dfc-brand-primary) !important;
            }

            .action-tile:where(:not(:disabled):not(.disabled)):active {
                transform: scale(0.98);
            }
            .action-tile:where(:not(:disabled):not(.disabled)):active .action-icon-wrapper {
                background-color: var(--dfc-brand-accent) !important;
            }
            .action-tile:where(:not(:disabled):not(.disabled)):active .action-label {
                color: var(--dfc-brand-accent) !important;
            }
        </style>
    `;

    // Listeners for transaction actions
    if (!isActionLocked) {
        container.querySelectorAll('.action-tile[data-action]').forEach(btn => {
            btn.onclick = () => openDrugForm(btn.dataset.action);
        });

        // 1. Attach Start Audit Listener IMMEDIATELY
        // 1. Attach Start Audit Listener IMMEDIATELY
        const startBtn = document.getElementById('btn-start-audit-action');
        if (startBtn) {
            startBtn.onclick = async () => {
                // Disable while processing & show loading intent
                if (startBtn.disabled) {return;}
                const originalContent = startBtn.innerHTML;

                try {
                    startBtn.disabled = true;
                    // Keep layout but add spinner
                    const label = startBtn.querySelector('.action-label');
                    if (label) {label.textContent = "Checking...";}
                    startBtn.classList.add('cursor-wait');

                    // PRE-FLIGHT CHECK
                    const checkResult = await checkPendingApprovals();

                    if (checkResult.pendingApprovals && checkResult.pendingApprovals.length > 0) {
                        // Found blocking approvals - Duplicate logic from handleStartAuditClick for consistency

                        // Sort Newest First (Desc)
                        checkResult.pendingApprovals.sort((a, b) => {
                            const dateA = new Date(a.createdAt?._seconds ? a.createdAt._seconds * 1000 : a.createdAt);
                            const dateB = new Date(b.createdAt?._seconds ? b.createdAt._seconds * 1000 : b.createdAt);
                            return dateB - dateA;
                        });

                        const listHtml = checkResult.pendingApprovals.map(a => {
                            const tx = a.payload || {};
                            let drugName = 'Unknown Drug';
                            let locationName = 'Unknown Location';

                            // 1. Resolve Drug & Location
                            let item = null;
                            if (Array.isArray(tx.payload) && tx.payload.length > 0) {
                                item = tx.payload[0];
                            }

                            if (item) {
                                const drugObj = (appData.drugInventory || []).find(d => d.id === item.drugId);
                                if (drugObj) {drugName = drugObj.name;}
                                const locId = item.location || item.fromLocation || item.toLocation;
                                const locObj = (appData.config?.drugs?.locations || []).find(l => l.id === locId);
                                if (locObj) {locationName = locObj.name;}
                            }

                            // 2. Resolve Type
                            const type = tx.type ? (tx.type.charAt(0).toUpperCase() + tx.type.slice(1)) : 'Transaction';

                            // 3. Resolve Date
                            let dateStr = 'Unknown Date';
                            try {
                                const dateVal = tx.transactionDate || a.createdAt;
                                dateStr = formatDate(dateVal);
                            } catch (e) { void e; }

                            // 4. Resolve Approver
                            let approverText = 'Pending Approval';
                            if (a.approverIds && Array.isArray(a.approverIds) && a.approverIds.length > 0) {
                                const names = a.approverIds.map(uid => {
                                    const u = (appData.users || []).find(user => user.id === uid);
                                    return u ? sanitizeHTML(u.name) : 'Unknown User';
                                });
                                approverText = `Waiting for ${names.join(', ')}`;
                            } else if (a.currentApproverRole) {
                                const roleObj = (appData.drugRoles || []).find(r => r.id === a.currentApproverRole);
                                approverText = roleObj ? `Waiting for ${roleObj.name}` : `Waiting for ${sanitizeHTML(a.currentApproverRole)}`;
                            }

                            return `<li class="mb-2 p-3 bg-white rounded border border-gray-200 shadow-sm flex flex-col justify-between">
                                    <div class="text-sm text-gray-900 mb-1">
                                        <span class="font-bold">${sanitizeHTML(type)}:</span>
                                        ${sanitizeHTML(drugName)} from ${sanitizeHTML(locationName)} on <span>${dateStr}</span>
                                    </div>
                                    <div class="text-xs text-gray-500 font-medium">
                                        ${sanitizeHTML(approverText)}
                                    </div>
                                </li>`;
                        }).join('');

                        openConfirmationModal(
                            'Audit Blocked',
                            `<div class="text-left">
                                    <p class="mb-3 text-gray-700 text-base">You cannot start an audit until the following items are resolved:</p>
                                    <ul class="list-none space-y-2 bg-gray-50 p-4 rounded border border-gray-200 h-96 overflow-y-auto">
                                        ${listHtml}
                                    </ul>
                                 </div>`,
                            () => { },
                            false,
                            'OK'
                        );

                        // Widen Modal
                        const modalEl = document.getElementById('confirm-modal');
                        if (modalEl) {
                            const inner = modalEl.querySelector('div');
                            if (inner) {
                                inner.classList.remove('max-w-sm');
                                inner.classList.add('max-w-2xl');
                            }
                        }
                        return; // Halt execution
                    }

                    // Checks Passed -> Start Audit
                    const result = await startAudit();

                    if (result && result.success) {
                        // Reset modal width just in case
                        const modalEl = document.getElementById('confirm-modal');
                        if (modalEl) {
                            const inner = modalEl.querySelector('div');
                            if (inner) {
                                inner.classList.add('max-w-sm');
                                inner.classList.remove('max-w-2xl');
                            }
                        }

                        renderSubmissionFeedback(result, 'audit_start');

                        if (result.approvalMode === 'bypass' || result.approvalMode === 'automatic_approval') {
                            state.drugsView = 'audit';
                            if (typeof renderDrugsView === 'function') {
                                renderDrugsView();
                            } else {
                                document.querySelector('[data-view="drugs"]')?.click();
                                setTimeout(() => document.getElementById('btn-drug-audit')?.click(), 100);
                            }
                        }
                    }

                } catch (err) {
                    console.error("Start audit checking failed:", err);
                    openConfirmationModal(
                        'Error',
                        `Unable to verify system status: ${err.message}. Please try again.`,
                        () => { },
                        false,
                        'OK'
                    );
                } finally {
                    if (startBtn) {
                        startBtn.disabled = false;
                        startBtn.classList.remove('cursor-wait');
                        // Restore Label
                        startBtn.innerHTML = originalContent;
                    }
                }
            };
        }

        // 2. Async Check for Pending Audit to disable button later
        if (canStartAudit) {
            checkPendingApprovals().then(result => {
                const pendingStart = result?.pendingApprovals?.find(a => a.workflowType === 'drug_audit_start');
                // Re-fetch button to be safe
                const btn = document.getElementById('btn-start-audit-action');

                if (btn && pendingStart) {
                    // DISABLE IT
                    btn.disabled = true;
                    btn.classList.add('opacity-50', 'cursor-not-allowed', 'disabled');
                    btn.classList.remove('cursor-pointer');
                    btn.title = "Audit start requested (Pending Approval)";

                    // Update label to indicate pending
                    const label = btn.querySelector('.action-label');
                    if (label) {label.textContent = "Start Audit";}
                    // Update Icon color
                    const icon = btn.querySelector('.material-symbols-outlined');
                    if (icon) {icon.classList.add('text-gray-400');}

                    // Remove click listener
                    btn.onclick = null;
                }
            }).catch(err => console.error("Error updating action centre status:", err));
        }
    }

}
